import { reconcileActiveVisualLabProfileAfterProductionChange } from "./visualLabService";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";

export type VisionPoint = { x: number; y: number };

export type VisionGroundTruthStatus = "open" | "verified" | "no_label";
export type VisionGroundTruthQuality = "precise" | "reconstructed" | "unusable";
export type VisionGroundTruthConditionTag = "damaged_frame" | "occluded" | "glare" | "low_contrast" | "dirty";

export interface VisionGroundTruthListItem {
  case_ref: string;
  source_type: "withdrawal" | "vo_capture";
  display_id: string;
  capture_origin: "production_withdrawal" | "vo_bootstrap";
  withdrawal_id: number | null;
  sid: number | null;
  vo_capture_uuid: string | null;
  withdrawal_number: number | null;
  username: string;
  material_number: string | null;
  withdrawal_date: string | null;
  app_version: string | null;
  detection_source_file_id: number;
  debug_data_file_id: number | null;
  ground_truth_file_id: number | null;
  ground_truth_status: VisionGroundTruthStatus;
  dataset_split: "training" | "dev" | "validation" | null;
  dataset_split_reasons: string[];
  annotation_quality: VisionGroundTruthQuality | null;
  condition_tags: VisionGroundTruthConditionTag[];
  active_learning_score: number;
  active_learning_priority: "none" | "normal" | "medium" | "high";
  active_learning_reasons: string[];
  updated_at: string;
}

export interface VisionCandidate {
  index: number;
  source: string;
  method: string;
  corners: VisionPoint[];
  accepted: boolean;
  selected: boolean;
  combined_score: number | null;
  reference_score: number | null;
}

export interface VisionGroundTruthData {
  schema_version: number;
  corner_space: "detection_source_normalized";
  case_ref: string | null;
  source_type: "withdrawal" | "vo_capture";
  sid: number | null;
  vo_capture_uuid: string | null;
  label_present: boolean;
  corners: VisionPoint[];
  verified: boolean;
  source: string;
  created_at: string;
  updated_by_user_id: number | null;
  annotation_quality: VisionGroundTruthQuality;
  condition_tags: VisionGroundTruthConditionTag[];
}

export interface VisionGroundTruthDetail {
  item: VisionGroundTruthListItem;
  detection_source_url: string;
  production_corners: VisionPoint[];
  candidates: VisionCandidate[];
  ground_truth: VisionGroundTruthData | null;
}

const VISION_HEAVY_READ_TIMEOUT_MS = 30000;

async function api<T>(url: string, init?: RequestInit, timeoutMs = 8000): Promise<T> {
  const jsonBody = Boolean(init?.body) && !(typeof FormData !== "undefined" && init?.body instanceof FormData);
  const response = await fetchWithTimeout(url, {
    credentials: "include",
    headers: {
      ...(jsonBody ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
    ...init,
  }, timeoutMs);
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const payload = await response.json() as { detail?: string };
      if (payload.detail) message = payload.detail;
    } catch { /* keep fallback */ }
    throw new Error(message);
  }
  return response.json() as Promise<T>;
}

export interface VisionPagedResult<T> { items: T[]; page: number; page_size: number; total: number; total_pages: number; has_previous: boolean; has_next: boolean; }
export interface VisionInterimChampionHistoryItem {
  schema_version: number; history_id: string; status: string; validation_pending: boolean; scope: string; phase: string;
  cycle_id: string | null; cycle_number?: number | null; learning_round: number; dataset_snapshot_id: string | null;
  optuna_trial_number: number | null; optuna_trial_display_id: number | null; search_attempt_found: number | null; search_attempt_total: number | null;
  study_name: string | null; study_generation: string | null; study_objective: string | null;
  objective: number | null; improvement: number | null; improvement_unit: string | null; baseline_metric_key: string | null; baseline_metric_label: string | null;
  comparison_metric_id?: string | null; comparison_metric_label?: string | null; comparison_scope?: string | null;
  baseline_value: number | null; candidate_value: number | null; baseline_sample_count: number; comparison_sample_count: number; baseline_scope: string | null; baseline_scope_label: string | null;
  hard_cases_solved: number; regressions: number; sample_count: number; found_at: string | null; recorded_at: string | null; seeded_from_pre_v01873_current_state: boolean;
}
export interface VisionGroundTruthStats { total: number; verified: number; no_label: number; open: number; autopilot_eligible: number; autopilot_required: number; autopilot_ready: boolean; active_learning_recommended: number; active_learning_deferred: number; excluded_missing_debug_data: number; excluded_missing_source: number; excluded_unusable: number; }

export interface VisionCaptureWorkflowData {
  withdrawal_date: string;
  material_number: string;
  material_text: string;
  material_type: string | null;
  storage_address: string;
  quantity_removed: string;
  current_stock: string;
  accounting_system: string | null;
  accounting_subarea: string | null;
  accounting_number: string | null;
  order_info: string | null;
  persons: string[];
  app_version: string | null;
}

export interface VisionCaptureFileInfo {
  id: number;
  file_type: string;
  original_filename: string;
  mime_type: string;
  sha256: string;
  size_bytes: number;
  created_at: string;
}

export interface VisionCaptureHistoryItem {
  uuid: string;
  case_ref: string;
  display_id: string;
  capture_origin: string;
  status: string;
  created_by_user_id: number;
  username: string;
  app_version: string | null;
  workflow: VisionCaptureWorkflowData;
  ground_truth_status: VisionGroundTruthStatus;
  annotation_quality: VisionGroundTruthQuality | null;
  condition_tags: VisionGroundTruthConditionTag[];
  file_count: number;
  files: VisionCaptureFileInfo[];
  created_at: string;
  updated_at: string;
}

export interface VisionBootstrapCapturePolicy {
  available_capture_cases: number;
  verified_ground_truth: number;
  target_ground_truth: number;
  threshold_reached: boolean;
  override_enabled: boolean;
  available: boolean;
  updated_at: string | null;
  updated_by_user_id: number | null;
}

export function fetchVisionCaptureHistoryPage(page = 1, pageSize = 10, query = ""): Promise<VisionPagedResult<VisionCaptureHistoryItem>> {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (query.trim()) params.set("q", query.trim());
  return api<VisionPagedResult<VisionCaptureHistoryItem>>(`/api/admin/vision-captures?${params.toString()}`);
}
export function fetchVisionCaptureHistoryItem(uuid: string): Promise<VisionCaptureHistoryItem> {
  return api<VisionCaptureHistoryItem>(`/api/admin/vision-captures/${encodeURIComponent(uuid)}`);
}
export function visionCaptureFileUrl(uuid: string, fileId: number): string {
  return `/api/admin/vision-captures/${encodeURIComponent(uuid)}/files/${fileId}/content`;
}

export function fetchVisionGroundTruthStats(): Promise<VisionGroundTruthStats> { return api<VisionGroundTruthStats>("/api/admin/vision-optimizer/ground-truth-stats"); }
export function fetchVisionBootstrapCapturePolicy(): Promise<VisionBootstrapCapturePolicy> { return api<VisionBootstrapCapturePolicy>("/api/admin/vision-optimizer/bootstrap-capture-policy"); }
export function updateVisionBootstrapCapturePolicy(overrideEnabled: boolean): Promise<VisionBootstrapCapturePolicy> {
  return api<VisionBootstrapCapturePolicy>("/api/admin/vision-optimizer/bootstrap-capture-policy", {
    method: "PUT",
    body: JSON.stringify({ override_enabled: overrideEnabled }),
  });
}
export function fetchVisionGroundTruthPage(page = 1, pageSize = 10, status?: VisionGroundTruthStatus | "all", query?: string): Promise<VisionPagedResult<VisionGroundTruthListItem>> {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (status && status !== "all") params.set("status", status);
  if (query?.trim()) params.set("query", query.trim());
  return api<VisionPagedResult<VisionGroundTruthListItem>>(`/api/admin/vision-optimizer/ground-truth-page?${params.toString()}`);
}
export function fetchVisionGroundTruthItems(): Promise<VisionGroundTruthListItem[]> { return fetchVisionGroundTruthPage(1, 10).then((result) => result.items); }

export function fetchVisionNextOpenGroundTruth(excludeRef?: string | null): Promise<VisionGroundTruthListItem | null> {
  const params = new URLSearchParams();
  if (excludeRef) params.set("exclude_ref", excludeRef);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return api<VisionGroundTruthListItem | null>(`/api/admin/vision-optimizer/ground-truth-next-open${suffix}`);
}

export function fetchVisionGroundTruthDetail(caseRef: string): Promise<VisionGroundTruthDetail> {
  const params = new URLSearchParams({ case_ref: caseRef });
  return api<VisionGroundTruthDetail>(`/api/admin/vision-optimizer/ground-truth-case?${params.toString()}`);
}

export function saveVisionGroundTruth(
  caseRef: string,
  payload: { label_present: boolean; corners: VisionPoint[]; verified: boolean; annotation_quality: VisionGroundTruthQuality; condition_tags: VisionGroundTruthConditionTag[] },
): Promise<VisionGroundTruthData> {
  const params = new URLSearchParams({ case_ref: caseRef });
  return api<VisionGroundTruthData>(`/api/admin/vision-optimizer/ground-truth-case?${params.toString()}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}


export interface VisionOptimizerControlState {
  enabled: boolean;
  pause_requested: boolean;
  resume_on_worker_restart: boolean;
  pause_reason: string | null;
  paused_at: string | null;
  updated_at: string | null;
  updated_by_user_id: number | null;
}

export function fetchVisionOptimizerControl(): Promise<VisionOptimizerControlState> {
  return api<VisionOptimizerControlState>("/api/admin/vision-optimizer/control");
}

export function updateVisionOptimizerControl(enabled: boolean): Promise<VisionOptimizerControlState> {
  return api<VisionOptimizerControlState>("/api/admin/vision-optimizer/control", {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });
}

export function pauseVisionOptimizerForRestart(reason = "Wartung / Deployment"): Promise<VisionOptimizerControlState> {
  return api<VisionOptimizerControlState>("/api/admin/vision-optimizer/control/pause-for-restart", { method:"POST", body:JSON.stringify({reason,resume_on_worker_restart:true}) });
}
export function cancelVisionOptimizerPause(): Promise<VisionOptimizerControlState> {
  return api<VisionOptimizerControlState>("/api/admin/vision-optimizer/control/cancel-pause", { method:"POST" });
}


export type VisionEvaluationClassification = "tp" | "fn" | "fp" | "tn" | "unevaluated";

export interface VisionEvaluationCandidateMetric {
  index: number;
  source: string;
  method: string;
  accepted: boolean;
  selected: boolean;
  mean_corner_error_norm: number | null;
  mean_corner_error_px: number | null;
  max_corner_error_px: number | null;
  iou: number | null;
}

export interface VisionEvaluationSample {
  case_ref: string;
  source_type: "withdrawal" | "vo_capture";
  display_id: string;
  capture_origin: "production_withdrawal" | "vo_bootstrap";
  withdrawal_id: number | null;
  sid: number | null;
  vo_capture_uuid: string | null;
  withdrawal_number: number | null;
  username: string;
  material_number: string | null;
  app_version: string | null;
  classification: VisionEvaluationClassification;
  label_present: boolean;
  production_detected: boolean;
  mean_corner_error_norm: number | null;
  mean_corner_error_px: number | null;
  max_corner_error_px: number | null;
  iou: number | null;
  processing_time_ms: number | null;
  best_candidate_index: number | null;
  best_candidate_iou: number | null;
  best_candidate_mean_corner_error_px: number | null;
  better_candidate_available: boolean;
  candidates: VisionEvaluationCandidateMetric[];
  annotation_quality: VisionGroundTruthQuality;
  condition_tags: VisionGroundTruthConditionTag[];
}

export interface VisionEvaluationCohort {
  key: string;
  label: string;
  sample_count: number;
  positive_count: number;
  detection_rate: number | null;
  mean_iou: number | null;
  mean_corner_error_px: number | null;
  false_negative: number;
}

export interface VisionEvaluationSummary {
  schema_version: number;
  scoring_version: string;
  dataset_version: string;
  evaluated_at: string;
  total_ground_truth: number;
  positive_ground_truth: number;
  negative_ground_truth: number;
  true_positive: number;
  false_negative: number;
  false_positive: number;
  true_negative: number;
  detection_rate: number | null;
  mean_iou: number | null;
  mean_corner_error_px: number | null;
  p95_corner_error_px: number | null;
  max_corner_error_px: number | null;
  mean_processing_time_ms: number | null;
  better_candidate_cases: number;
  assessment_code: "insufficient_data" | "critical" | "needs_improvement" | "promising" | "strong";
  assessment_label: string;
  assessment_summary: string;
  samples: VisionEvaluationSample[];
  cohorts: VisionEvaluationCohort[];
  sample_page: number; sample_page_size: number; sample_total: number; sample_total_pages: number; sample_has_previous: boolean; sample_has_next: boolean;
}

export function fetchVisionEvaluation(page = 1, pageSize = 10): Promise<VisionEvaluationSummary> {
  return api<VisionEvaluationSummary>(`/api/admin/vision-optimizer/evaluation?page=${page}&page_size=${pageSize}`);
}

export interface VisionParameterDefinition {
  key: string;
  label: string;
  category: string;
  description: string;
  baseline_value: number | string | boolean | null;
  featured: boolean;
  runtime_surface: string | null;
  runtime_required: boolean;
  auto_tunable: boolean;
  admin_editable: boolean;
  fixed_invariant: boolean;
  fingerprint: boolean;
}

export interface VisionParameterCatalog {
  source_sid: number | null;
  app_version: string | null;
  parameter_count: number;
  runtime_catalog_version: string | null;
  runtime_catalog_sha256: string | null;
  parameters: VisionParameterDefinition[];
}

export function fetchVisionParameterCatalog(): Promise<VisionParameterCatalog> {
  return api<VisionParameterCatalog>("/api/admin/vision-optimizer/parameter-catalog");
}

export interface VisionParameterSet {
  name: string;
  description: string | null;
  overrides: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  updated_by_user_id: number | null;
}

export function fetchVisionParameterSets(): Promise<VisionParameterSet[]> {
  return api<VisionParameterSet[]>("/api/admin/vision-optimizer/parameter-sets");
}

export function saveVisionParameterSet(payload: { name: string; description: string | null; overrides: Record<string, unknown> }): Promise<VisionParameterSet> {
  return api<VisionParameterSet>("/api/admin/vision-optimizer/parameter-sets", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteVisionParameterSet(name: string): Promise<{ deleted: boolean; name: string }> {
  return api<{ deleted: boolean; name: string }>(`/api/admin/vision-optimizer/parameter-sets/${encodeURIComponent(name)}`, { method: "DELETE" });
}


export type VisionOptimizerJobStatus = "queued" | "retry_wait" | "running" | "completed" | "paused" | "failed" | "blocked" | "cancelled";
export interface VisionDatasetState {
  schema_version: number;
  dataset_version: string;
  frozen: boolean;
  ready: boolean;
  total_ground_truth: number;
  training_count: number;
  validation_count: number;
  core_gs_count: number;
  regression_bank_count: number;
  unassigned_count: number;
  min_ground_truth: number;
  created_at: string | null;
  updated_at: string | null;
  note: string | null;
  split_strategy: string | null;
  split_strategy_version: number | null;
  goldstandard_selection_summary: Record<string, unknown>;
  immutable: boolean;
  goldstandard_version: number;
  goldstandard_label: string;
  last_rebuilt_at: string | null;
  last_rebuild_changed: number;
}
export function fetchVisionDatasetState(): Promise<VisionDatasetState> { return api<VisionDatasetState>("/api/admin/vision-optimizer/dataset-state"); }

export interface VisionGoldstandardItem { case_ref:string; source_type:"withdrawal"|"vo_capture"; display_id:string; capture_origin:"production_withdrawal"|"vo_bootstrap"; withdrawal_id:number|null; sid:number|null; vo_capture_uuid:string|null; material_number:string|null; annotation_quality:VisionGroundTruthQuality|null; condition_tags:string[]; selection_reasons:string[]; }
export interface VisionGoldstandardState { frozen:boolean; version:number; label:string; dataset_version:string; created_at:string|null; updated_at:string|null; split_strategy:string; training_count:number; gs_count:number; candidate_count:number; selection_summary:Record<string,unknown>; items:VisionGoldstandardItem[]; }
export interface VisionGoldstandardRebuildResult { ok:boolean; message:string; previous_version:number; new_version:number; kept:number; replaced:number; state:VisionGoldstandardState; }
export function fetchVisionGoldstandard(): Promise<VisionGoldstandardState> { return api<VisionGoldstandardState>("/api/admin/vision-optimizer/goldstandard"); }
export function rebuildVisionGoldstandard(): Promise<VisionGoldstandardRebuildResult> { return api<VisionGoldstandardRebuildResult>("/api/admin/vision-optimizer/goldstandard/rebuild", {method:"POST",body:JSON.stringify({confirm:true})}); }

export interface VisionRegressionBankItem { case_ref:string; source_type:"withdrawal"|"vo_capture"; display_id:string; capture_origin:"production_withdrawal"|"vo_bootstrap"; sid:number|null; vo_capture_uuid:string|null; protection_status:"protected"|"active_regression"; reasons:string[]; first_added_at:string|null; last_seen_at:string|null; solved_count:number; regression_count:number; }
export interface VisionRegressionBankState { schema_version:number; version:number; total:number; protected:number; active_regressions:number; updated_at:string|null; items:VisionRegressionBankItem[]; }
export function fetchVisionRegressionBank(): Promise<VisionRegressionBankState> { return api<VisionRegressionBankState>("/api/admin/vision-optimizer/regression-bank"); }

export interface VisionCycleReport { schema_version:number; report_id:string; cycle_id:string; cycle_number:number; status:"running"|"waiting_for_admin"|"completed"|"cancelled"|"interrupted"|"superseded"|"legacy_incomplete"|"error"; dataset_version:string|null; dataset_snapshot_id:string|null; dataset_snapshot:Record<string,unknown>; case_lifecycle_summary:Record<string,unknown>; regression_bank_summary:Record<string,unknown>; guard_summary:Record<string,unknown>; gs_version:number; learning_round:number; completed_rounds:number; started_at:string|null; finished_at:string|null; recommendation_code:"approve"|"review"|"no_candidate"|"insufficient_data"|"cancelled"|"interrupted"|"legacy_incomplete"; recommendation_label:string; summary:string; production_changed:boolean; candidate_id:string|null; best_round:number|null; total_improvement:number|null; component_results:Record<string,unknown>; failure_summary:Record<string,unknown>; round_history:Array<Record<string,unknown>>; next_cycle:Record<string,unknown>; }
export function fetchLatestVisionCycleReport(): Promise<VisionCycleReport|null> { return api<VisionCycleReport|null>("/api/admin/vision-optimizer/cycle-report/latest"); }
export function fetchVisionCycleReports(limit=20): Promise<VisionCycleReport[]> { const safe=Math.max(1,Math.min(100,Math.trunc(limit))); return api<VisionCycleReport[]>(`/api/admin/vision-optimizer/cycle-reports?limit=${safe}`); }
export function fetchVisionCycleReportsPage(page=1,pageSize=10): Promise<VisionPagedResult<VisionCycleReport>> { const safePage=Math.max(1,Math.trunc(page)); const safeSize=Math.max(1,Math.min(50,Math.trunc(pageSize))); return api<VisionPagedResult<VisionCycleReport>>(`/api/admin/vision-optimizer/cycle-reports-page?page=${safePage}&page_size=${safeSize}`, undefined, VISION_HEAVY_READ_TIMEOUT_MS); }
export function fetchVisionCycleReportDetails(cycleId:string): Promise<VisionCycleReport> { return api<VisionCycleReport>(`/api/admin/vision-optimizer/cycle-reports/${encodeURIComponent(cycleId)}/details`, { cache:"no-store" }, VISION_HEAVY_READ_TIMEOUT_MS); }
export function fetchVisionCycleRoundHistory(cycleId:string): Promise<Array<Record<string,unknown>>> { return api<Array<Record<string,unknown>>>(`/api/admin/vision-optimizer/cycle-reports/${encodeURIComponent(cycleId)}/round-history`, { cache:"no-store" }, VISION_HEAVY_READ_TIMEOUT_MS); }

export interface VisionOptimizerJobState {
  id: string;
  type: "ab" | "sweep" | "dataset_baseline" | "optuna" | "cnn_training" | "hybrid_validation" | "fast_path_validation" | "ocr_optimization" | "domain_ocr_training" | "neural_child_evolution" | "confidence_calibration" | "adaptive_field_rescue" | "geometry_neural_hpo" | "end_to_end_validation" | "candidate_shadow_validation" | "live_training" | "live_validation";
  scope: "pipeline" | "live";
  status: VisionOptimizerJobStatus;
  state_revision?: number;
  created_at: string;
  queued_at?: string | null;
  started_at: string | null;
  finished_at: string | null;
  request: Record<string, unknown>;
  progress_current: number;
  progress_total: number;
  completed_units?: number;
  active_unit?: number | null;
  progress_kind?: "trial" | "epoch" | "item" | "step" | "candidate" | "child" | "calibration" | "plan" | "hpo_run" | "policy" | "case" | "sample" | "batch" | "session" | "frame" | "field" | "pass" | "finalization" | "dataset" | null;
  progress_phase?: string | null;
  progress_subphase?: string | null;
  finalization_pending?: boolean;
  message: string | null;
  result: Record<string, unknown> | null;
  result_summary?: Record<string, unknown> | null;
  checkpoint_ref?: Record<string, unknown> | null;
  result_ref?: Record<string, unknown> | null;
  interim_result?: Record<string, unknown>;
  attempt_count?: number;
  max_attempts?: number;
  next_attempt_at?: string | null;
  last_error?: string | null;
  blocked_reason?: string | null;
  retry_classification?: string | null;
  recovery_count?: number;
  recovery_pending?: boolean;
  resource_recycle_count?: number;
  resource_recycle_sid?: number | null;
  resource_recycle_same_sid_count?: number;
  resource_preflight_recycle_count?: number;
  resource_recycle_progress_key?: string | null;
  resource_recycle_same_progress_count?: number;
  legacy_resource_resume_migrated?: boolean;
  nested_resource_recycle_migrated?: boolean;
  baseline_checkpoint?: Record<string, unknown>;
  heartbeat_at?: string | null;
  worker_id?: string | null;
  activity_stage?: string | null;
  activity_case_ref?: string | null;
  activity_source_type?: "withdrawal" | "vo_capture" | null;
  activity_display_id?: string | null;
  activity_sid?: number | null;
  activity_detail?: string | null;
  activity_progress_current?: number | null;
  activity_progress_total?: number | null;
  activity_progress_kind?: string | null;
  activity_progress_label?: string | null;
  activity_app_version?: string | null;
  activity_capture_origin?: string | null;
  activity_compatibility?: string | null;
  activity_compatibility_reason?: string | null;
  activity_updated_at?: string | null;
  activity_history?: Array<{at:string;stage?:string|null;case_ref?:string|null;source_type?:"withdrawal"|"vo_capture"|null;display_id?:string|null;sid?:number|null;detail?:string|null;message?:string|null;activity_progress_current?:number|null;activity_progress_total?:number|null;activity_progress_kind?:string|null;activity_progress_label?:string|null}>;
  optuna_study_name?: string | null;
  optuna_study_generation?: string | null;
  optuna_trial_number?: number | null;
  optuna_trial_display_id?: number | null;
  search_attempt_current?: number | null;
  search_attempt_total?: number | null;
  completed_trials?: number | null;
  trial_total?: number | null;
}
export function createVisionOptimizerJob(payload: { type: "ab" | "sweep" | "dataset_baseline" | "optuna" | "cnn_training" | "hybrid_validation" | "fast_path_validation" | "ocr_optimization" | "end_to_end_validation" | "candidate_shadow_validation" | "live_training" | "live_validation"; parameter_set_a?: string; parameter_set_b?: string | null; limit?: number | null; sweep_parameter?: string | null; sweep_values?: number[]; dataset_split?: "all" | "training" | "dev" | "validation" | "rolling_validation" | "round_guard" | "sealed_gate" | "regression" | "hard_guard"; optuna_trials?: number; cnn_epochs?: number; fast_path_confidence_threshold?: number; optuna_mode?: "opencv" | "hybrid" | "ocr"; search_space_keys?: string[]; learning_focus_key?: string | null; failure_driven_pruning?: boolean; cnn_input_scale?: number; threshold_overrides?: Record<string, unknown>; candidate_threshold_overrides?: Record<string, unknown>; candidate_config?: Record<string, unknown>; skip_raw_warp?: boolean }): Promise<VisionOptimizerJobState> {
  return api<VisionOptimizerJobState>("/api/admin/vision-optimizer/jobs", { method: "POST", body: JSON.stringify(payload) });
}
export function fetchVisionOptimizerJobs(): Promise<VisionOptimizerJobState[]> { return api<VisionOptimizerJobState[]>("/api/admin/vision-optimizer/jobs"); }
export interface VisionOptimizerJobStatisticsSummary {
  schema_version: number;
  contract_version: string;
  generated_at: string;
  job_count: number;
  total_duration_seconds: number;
  longest: { job: VisionOptimizerJobState; seconds: number } | null;
  total_trials: number;
  opencv_trials: number;
  hybrid_trials: number;
  ocr_trials: number;
  trial_heavy: { job: VisionOptimizerJobState; count: number } | null;
  cnn_epochs: number;
  retries: number;
  recoveries: number;
  recycles: number;
  pruned: number;
  most_recovered: VisionOptimizerJobState | null;
  completed_jobs: number;
  failed_jobs: number;
  blocked_jobs: number;
  cancelled_jobs: number;
  most_common_job: { type: string; job: VisionOptimizerJobState | null; count: number } | null;
  source?: string;
  workunit_lifetime: {
    schema_version: number;
    contract_version: string;
    total_completed_workunits: number;
    attributed_completed_workunits: number;
    legacy_total_floor: number;
    unattributed_legacy_completed_workunits: number;
    completed_workplans: number;
    recorded_rounds: number;
    completed_cycles: number;
    last_committed_workplan_id: string | null;
    active_round_key: string | null;
    current_workplan_completed_units: number;
    updated_at: string | null;
    backfill_complete: boolean;
    backfill_version: string | null;
    backfill_audit: { cycles_scanned:number; rounds_exact:number; rounds_reconstructed:number; unresolved_rounds:number; legacy_v1_entries_migrated:number };
    cycle_totals: Array<{ cycle_id:string|null; cycle_number:number|null; completed_workunits:number; rounds:number; exact_rounds:number; reconstructed_rounds:number }>;
  };
}

export function fetchVisionOptimizerJobStatistics(): Promise<VisionOptimizerJobStatisticsSummary> {
  return api<VisionOptimizerJobStatisticsSummary>("/api/admin/vision-optimizer/statistics/job-summary", undefined, VISION_HEAVY_READ_TIMEOUT_MS);
}
export function fetchVisionOptimizerStatisticsActivitySnapshot(signal?: AbortSignal): Promise<VisionOptimizerActivitySnapshot> { return fetchVisionOptimizerActivitySnapshot(signal, VISION_HEAVY_READ_TIMEOUT_MS); }

export function fetchVisionOptimizerJobsPage(page = 1, state?: "active" | "history"): Promise<VisionPagedResult<VisionOptimizerJobState>> { const params=new URLSearchParams({page:String(page),page_size:"10"}); if(state) params.set("state",state); return api<VisionPagedResult<VisionOptimizerJobState>>(`/api/admin/vision-optimizer/jobs-page?${params.toString()}`, undefined, VISION_HEAVY_READ_TIMEOUT_MS); }
export function fetchVisionOptimizerJob(id: string, signal?: AbortSignal): Promise<VisionOptimizerJobState> { return api<VisionOptimizerJobState>(`/api/admin/vision-optimizer/jobs/${encodeURIComponent(id)}`, { signal, cache:"no-store" }); }
export function cancelVisionOptimizerJob(id: string): Promise<VisionOptimizerJobState> { return api<VisionOptimizerJobState>(`/api/admin/vision-optimizer/jobs/${encodeURIComponent(id)}/cancel`, { method: "POST" }); }
export function retryVisionOptimizerJob(id: string): Promise<VisionOptimizerJobState> { return api<VisionOptimizerJobState>(`/api/admin/vision-optimizer/jobs/${encodeURIComponent(id)}/retry`, { method: "POST" }); }
export function restartVisionOptimizerStep(id: string): Promise<VisionOptimizerJobState> { return api<VisionOptimizerJobState>(`/api/admin/vision-optimizer/jobs/${encodeURIComponent(id)}/restart-step`, { method: "POST" }); }

export type VisionAutopilotStatus = "disabled" | "waiting_for_ground_truth" | "waiting_for_data" | "idle" | "baseline" | "sweeping" | "training" | "validating" | "promotion" | "candidate_ready" | "waiting_for_admin" | "paused" | "cancelled" | "error";
export type VisionSupervisorState = "IDLE" | "LEARNING" | "QUALITY_MILESTONE_REACHED" | "DATA_LIMITED" | "RUNTIME_LIMITED" | "SEARCH_STAGNATED" | "SEARCH_SPACE_EXHAUSTED" | "SATURATED_WATCHING" | "DRIFT" | "CANDIDATE_READY" | "BLOCKED" | "PAUSED";
export type VisionAutopilotPhase = "data" | "baseline" | "search" | "training" | "validation" | "promotion" | "approval" | "paused" | "error";
export type VisionLearningPhaseLifecycleState = "unplanned" | "not_required" | "planned" | "active" | "validating" | "completed" | "reused" | "data_limited" | "regressed" | "technical_blocked";
export interface VisionLearningPhaseLifecycle { state: VisionLearningPhaseLifecycleState; label: string; reason: string; }
export interface VisionAutopilotState {
  enabled: boolean;
  status: VisionAutopilotStatus;
  phase: VisionAutopilotPhase;
  state_machine_version: string;
  supervisor_state_version: string;
  supervisor_state: VisionSupervisorState;
  supervisor_state_reason: string | null;
  supervisor_state_changed_at: string | null;
  supervisor_started_at: string | null;
  supervisor_reopen_count: number;
  last_supervisor_reopen_at: string | null;
  last_supervisor_reopen_reason: string | null;
  supervisor_replan_count?: number;
  last_supervisor_replan_at?: string | null;
  last_supervisor_replan_reason?: string | null;
  quality_milestone: { threshold?: number; metric?: string; reached?: boolean; current_rate?: number | null; first_reached_at?: string | null; terminal?: boolean; [key: string]: unknown };
  research_escalation?: { version?: string; level?: number; stage?: string; epoch?: number; replan_count?: number; budget_epoch?: number; budget_epoch_started_round?: number; legacy_budget_epoch_start_repaired?: boolean; last_advanced_at?: string | null; last_reason?: string | null; [key: string]: unknown };
  min_ground_truth: number;
  min_objective_improvement: number;
  pause_on_candidate: boolean;
  pause_on_candidate_explicit?: boolean;
  cycle_index: number;
  cycle_sequence: number;
  completed_cycles: number;
  current_job_id: string | null;
  current_parameter: string | null;
  last_candidate_id: string | null;
  pending_candidate_id: string | null;
  last_completed_phase: string | null;
  last_action_at: string | null;
  last_message: string | null;
  updated_at: string | null;
  updated_by_user_id: number | null;
  e2e_stage: string;
  e2e_cycle_id: string | null;
  e2e_components: Record<string, unknown>;
  e2e_opencv_parameter_set: string | null;
  e2e_hybrid_params: Record<string, unknown>;
  e2e_ocr_params: Record<string, unknown>;
  last_completed_ground_truth_count: number;
  last_completed_dataset_version: string | null;
  last_completed_cycle_id: string | null;
  e2e_live: Record<string, unknown>;
  learning_round: number;
  completed_learning_rounds: number;
  max_learning_rounds: number;
  stagnation_rounds: number;
  max_stagnation_rounds: number;
  round_history: Array<Record<string, unknown>>;
  best_cycle_score: number | null;
  last_round_improvement: number | null;
  new_ground_truth_per_cycle: number; // legacy compatibility; SR15 readiness is information-based
  new_ground_truth_since_last_cycle: number;
  new_ground_truth_required: number;
  legacy_gt_capability_version?: string | null;
  legacy_gt_capability_audit?: { counts?: Record<string, number>; total?: number; usable_total?: number; quarantined_total?: number; fingerprint_sha256?: string | null; generated_at?: string | null; [key:string]: unknown };
  information_readiness_version?: string | null;
  information_readiness?: { ready?: boolean; score?: number; confidence?: string; signals?: Array<Record<string, unknown>>; gaps?: string[]; new_or_changed_gt?: number; new_feedback?: number; user_corrections?: number; retake_signals?: number; new_condition_slices?: string[]; recommendation?: string; [key:string]: unknown };
  information_readiness_checked_at?: string | null;
  information_readiness_baseline_snapshot_id?: string | null;
  continuous_learning_version?: string | null;
  continuous_learning?: { mode?: string; information_ready?: boolean; information_score?: number; next_cycle_ready?: boolean; next_cycle_reason?: string | null; data_limited?: { active?: boolean; reason?: string | null; reason_code?: string | null; recommendation?: string; requested_cases_min?: number; requested_cases_max?: number; decision_key?: string | null; automatic_recovery?: boolean; automatic_recovery_ready?: boolean; recovery_evidence_kind?: string | null; recovery_evidence_changed?: boolean; circuit_eligible?: boolean; [key:string]: unknown }; drift?: { active?: boolean; status?: string; bundle_id?: string | null; reasons?: string[]; decision_key?: string | null; reopen_search?: boolean; circuit_eligible?: boolean; [key:string]: unknown }; circuit_eligible?: boolean; [key:string]: unknown };
  continuous_learning_checked_at?: string | null;
  data_limited_context?: { reason?: string; reason_code?: string; snapshot_id?: string | null; cycle_id?: string | null; automatic_recovery?: boolean; manual_action_required?: boolean; recovery_evidence_kind?: string | null; recovery_evidence_count?: number; recovery_evidence_sha256?: string | null; recovery_evidence_captured_at?: string | null; [key:string]: unknown } | null;
  workflow_step: number;
  workflow_label: string;
  failure_summary: Record<string, unknown>;
  latest_cycle_report_id: string | null;
  cycle_started_at: string | null;
  dataset_snapshot_id: string | null;
  last_completed_dataset_snapshot_id: string | null;
  dataset_snapshot: Record<string, unknown>;
  production_baseline: Record<string, unknown>;
  interim_champion: Record<string, unknown>;
  elite_population_version?: string;
  elite_population?: Record<string, unknown>;
  component_champions?: Record<string, unknown>;
  case_lifecycle_summary: Record<string, unknown>;
  e2e_candidate_config: Record<string, unknown>;
  e2e_candidate_config_sha256: string | null;
  candidate_integrity_version: string;
  runtime_parity_version?: string;
  runtime_parity_migration_pending?: boolean;
  long_term_learning_version?: string;
  long_term_learning_migration_pending?: boolean;
  live_learning_version?: string;
  live_learning_migration_pending?: boolean;
  worker_hardening_version?: string;
  worker_hardening_migration_pending?: boolean;
  learning_integrity_version?: string;
  dataset_governance_version?: string | null;
  rebaseline_generation_version?: string | null;
  rebaseline_generation_sha256?: string | null;
  rebaseline_generation?: Record<string, unknown>;
  evidence_compatibility_version?: string | null;
  evidence_compatibility_audit?: Record<string, unknown>;
  rebaseline_migration_pending?: boolean;
  rebaseline_migration_mode?: string | null;
  learning_integrity_migration_pending?: boolean;
  cnn_uncertainty_version?: string;
  cnn_uncertainty_migration_pending?: boolean;
  synthetic_training_version?: string;
  synthetic_training_migration_pending?: boolean;
  final_candidate_round?: number | null;
  regression_bank_summary?: Record<string, unknown>;
  orchestration_version?: string;
  orchestration_migration_pending?: boolean;
  learning_plan: Record<string, unknown>;
  phase_lifecycle?: Record<string, VisionLearningPhaseLifecycle>;
  round_workplan?: Record<string, unknown> | null;
  cycle_workplan?: Record<string, unknown> | null;
  round_started_at?: string | null;
  phase_attempts: Record<string, unknown>;
  phase_outcomes: Record<string, unknown>;
  convergence: Record<string, unknown>;
  e2e_cnn: Record<string, unknown>;
}
export function startNewVisionAutopilotCycle(): Promise<VisionAutopilotState> { return api<VisionAutopilotState>("/api/admin/vision-optimizer/autopilot/new-cycle", { method: "POST" }); }
export function fetchVisionAutopilot(): Promise<VisionAutopilotState> {
  return api<VisionAutopilotState>("/api/admin/vision-optimizer/autopilot");
}
export function fetchVisionInterimChampionHistory(page = 1, pageSize = 10): Promise<VisionPagedResult<VisionInterimChampionHistoryItem>> {
  return api<VisionPagedResult<VisionInterimChampionHistoryItem>>(`/api/admin/vision-optimizer/interim-champions?page=${page}&page_size=${pageSize}`, { cache:"no-store" }, VISION_HEAVY_READ_TIMEOUT_MS);
}
export function updateVisionAutopilot(payload: { enabled: boolean; min_ground_truth: number; min_objective_improvement: number; pause_on_candidate: boolean }): Promise<VisionAutopilotState> {
  return api<VisionAutopilotState>("/api/admin/vision-optimizer/autopilot", { method: "PUT", body: JSON.stringify(payload) });
}

export interface VisionCandidateSignificance {
  contract_version: string;
  primary_metric: "four_of_four_exact_rate";
  fixed_percentage_point_threshold: number | null;
  fixed_two_percentage_point_rule: boolean;
  percentage_only_decision: boolean;
  paired_same_case: boolean;
  paired_four_of_four: {
    sample_count: number;
    wins: number;
    losses: number;
    ties: number;
    both_exact: number;
    both_inexact: number;
    discordant_total: number;
    net_wins: number;
    exact_two_sided_p: number;
  };
  win_loss_tie: { wins: number; losses: number; ties: number };
  four_of_four_gain: number | null;
  remaining_error_reduction: {
    production_error_count: number;
    candidate_error_count: number;
    relative_reduction: number | null;
  };
  hard_case_gain: {
    sample_count: number;
    wins: number;
    losses: number;
    ties: number;
    net_wins: number;
    exact_two_sided_p: number;
  };
  field_regressions: { total: number; per_field: Record<string, number> };
  runtime: {
    production_mean_ms: number | null;
    candidate_mean_ms: number | null;
    relative_improvement: number | null;
  };
  uncertainty: {
    sample_count: number;
    discordant_pairs: number;
    exact_two_sided_p: number | null;
    paired_direction: "candidate" | "production" | "tie";
    reporting_band: string;
    warning: string | null;
  };
  novelty: {
    status: "first_candidate" | "new_config" | "same_config_new_evidence" | "repeat_without_new_evidence";
    previous_candidate_id: string | null;
    previous_candidate_config_sha256?: string | null;
    same_config: boolean;
    changed_leaf_count: number | null;
    total_leaf_count: number | null;
    change_ratio: number | null;
    new_evidence: boolean;
    changed_leaf_details?: Array<{ path: string; previous: string | null; current: string | null }>;
    changed_leaf_details_truncated?: boolean;
    previous_evidence?: {
      candidate_four_of_four_exact_rate: number | null;
      production_four_of_four_exact_rate: number | null;
      four_of_four_exact_delta: number | null;
      paired_net_wins: number | null;
      hard_case_net_wins: number | null;
      runtime_relative_improvement: number | null;
      sample_count: number;
      discordant_pairs: number;
    } | null;
  };
  decision: {
    offer_recommended: boolean;
    supported_offer_recommended?: boolean;
    exploratory_offer_recommended?: boolean;
    exploratory_positive_signal?: boolean;
    regression_free: boolean;
    evidence_sufficient: boolean;
    paired_supported: boolean;
    hard_case_supported: boolean;
    runtime_supported: boolean;
    supporting_signals: string[];
    explanation: string;
  };
}

export interface VisionAdminTestCandidate {
  schema_version: number;
  lifecycle_version: string;
  lifecycle_stage: "admin_test_candidate";
  id: string;
  research_champion_id: string;
  release_candidate_id: string | null;
  status: "offered" | "superseded" | "promoted_to_release_candidate";
  created_at: string;
  updated_at: string;
  source_job_id: string;
  cycle_id: string | null;
  learning_round: number;
  dataset_snapshot_id: string | null;
  candidate_config_sha256: string;
  candidate_config?: Record<string, unknown>;
  research_extensions?: Record<string, unknown>;
  research_extensions_sha256?: string | null;
  research_family?: string;
  candidate_tier?: "exploratory" | "supported" | "legacy_unknown";
  evidence_maturity?: "directional_exploratory" | "statistically_supported" | "legacy_unknown";
  recommendation_label: string;
  summary: string;
  activation_supported: false;
  production_eligible: false;
  production_changed: false;
  notified_at?: string | null;
  archive_state?: "active" | "archived";
  archived_at?: string | null;
  archived_reason?: string | null;
  live_test_count?: number;
  last_live_test_at?: string | null;
  live_test_supported: boolean;
  live_test_workpackage: string;
  final_release_gates_pending: boolean;
  shadow_required_later: boolean;
  offered_before_quality_milestone: boolean;
  quality_milestone_threshold: number;
  production_four_of_four_exact_rate: number | null;
  candidate_four_of_four_exact_rate: number | null;
  four_of_four_exact_delta: number | null;
  candidate_significance?: VisionCandidateSignificance | null;
}
export function fetchVisionAdminTestCandidates(): Promise<VisionAdminTestCandidate[]> {
  return api<VisionAdminTestCandidate[]>("/api/admin/vision-optimizer/admin-test-candidates");
}
export function fetchVisionAdminTestCandidatesPage(page = 1, archived = false): Promise<VisionPagedResult<VisionAdminTestCandidate>> {
  return api<VisionPagedResult<VisionAdminTestCandidate>>(`/api/admin/vision-optimizer/admin-test-candidates-page?page=${page}&page_size=10&archived=${archived ? "true" : "false"}`);
}
export function setVisionAdminTestCandidateArchived(id:string, archived:boolean):Promise<VisionAdminTestCandidate>{
  return api<VisionAdminTestCandidate>(`/api/admin/vision-optimizer/admin-test-candidates/${encodeURIComponent(id)}/archive`,{method:"POST",body:JSON.stringify({archived})});
}


export interface VisionCandidateLiveTestBundle {
  schema_version: 1 | 2;
  contract_id: "candidate-live-test-core-v1-sr38";
  candidate_id: string;
  candidate_config: Record<string, unknown>;
  candidate_config_sha256: string;
  candidate_pipeline_fingerprint: Record<string, unknown>;
  model_id: string | null;
  model_url: string | null;
  model_sha256: string | null;
  neural_ocr_shadow_supported?: boolean;
  neural_ocr_shadow_model_id?: string | null;
  neural_ocr_shadow_model_url?: string | null;
  neural_ocr_shadow_model_sha256?: string | null;
  neural_ocr_shadow_metadata?: Record<string, unknown>;
  local_only: true;
  same_raw_ab_required: true;
  activation_supported: false;
  production_changed: false;
  business_side_effects_allowed: false;
}

export interface VisionCandidateLiveRecognitionRun {
  role: "production" | "candidate";
  recognition_run_id: string;
  raw_sha256: string;
  fields: Record<string, string>;
  timings: Record<string, unknown>;
  confidence: Record<string, unknown>;
  geometry: Record<string, unknown>;
  ocr_passes: Array<Record<string, unknown>>;
  rescue: Record<string, unknown>;
  runtime: Record<string, unknown>;
  debug: Record<string, unknown>;
  provenance: Record<string, unknown>;
}

export interface VisionCandidateLiveNeuralOcrShadowRun {
  contract_id: "neural-ocr-admin-shadow-v1";
  raw_sha256: string;
  model_id: string;
  model_sha256: string;
  fields: Record<string, string>;
  confidence: Record<string, unknown>;
  timing: Record<string, unknown>;
  runtime: Record<string, unknown>;
  provenance: Record<string, unknown>;
  same_raw_ab: true;
  research_only: true;
  production_eligible: false;
  activation_supported: false;
  production_changed: false;
}

export interface VisionCandidateLiveTestCapturePayload {
  schema_version: 1;
  contract_id: "candidate-live-test-core-v1-sr38";
  capture_id: string;
  candidate_id: string;
  raw_sha256: string;
  same_raw_ab: true;
  created_at: string;
  production: VisionCandidateLiveRecognitionRun;
  candidate: VisionCandidateLiveRecognitionRun;
  neural_ocr_shadow?: VisionCandidateLiveNeuralOcrShadowRun | null;
  business_side_effects: Record<string, boolean>;
  production_changed: false;
  correction_scoring: Record<string, boolean | string>;
}

export interface VisionCandidateLiveTestCapture extends VisionCandidateLiveTestCapturePayload {
  persisted_at: string;
}

export function fetchVisionCandidateLiveTestBundle(candidateId: string): Promise<VisionCandidateLiveTestBundle> {
  return api<VisionCandidateLiveTestBundle>(`/api/admin/vision-optimizer/admin-test-candidates/${encodeURIComponent(candidateId)}/live-test-bundle`);
}

export function persistVisionCandidateLiveTestCapture(candidateId: string, payload: VisionCandidateLiveTestCapturePayload): Promise<VisionCandidateLiveTestCapture> {
  return api<VisionCandidateLiveTestCapture>(`/api/admin/vision-optimizer/admin-test-candidates/${encodeURIComponent(candidateId)}/live-test-captures`, { method: "POST", body: JSON.stringify(payload) });
}

export function fetchVisionCandidateLiveTestCaptures(candidateId: string, limit = 20): Promise<VisionCandidateLiveTestCapture[]> {
  return api<VisionCandidateLiveTestCapture[]>(`/api/admin/vision-optimizer/admin-test-candidates/${encodeURIComponent(candidateId)}/live-test-captures?limit=${Math.max(1, Math.min(100, Math.trunc(limit)))}`);
}


export type VisionCandidateLiveCorrectionSource = "unchanged" | "manual" | "material_master_exact" | "material_master_fuzzy";

export interface VisionCandidateLiveCorrectionField {
  predicted: string;
  final_admin_value: string;
  changed: boolean;
  correction_source: VisionCandidateLiveCorrectionSource;
}

export interface VisionCandidateLiveCorrectionPayload {
  schema_version: 1;
  contract_id: "candidate-live-test-ui-v1-sr39";
  capture_id: string;
  candidate_id: string;
  recognition_run_id: string;
  fields: Record<"materialText" | "materialType" | "materialNumber" | "storageAddress", VisionCandidateLiveCorrectionField>;
  assistance: {
    material_master_is_truth: false;
    automatic_replacement: false;
    explicit_admin_selection: boolean;
    selected_material_number?: string | null;
    selected_score?: number | null;
  };
  business_side_effects: Record<string, boolean>;
  production_changed: false;
  correction_scoring: {
    future_train_evidence_allowed: true;
    same_candidate_retroactive_score_allowed: false;
  };
  created_at: string;
}

export interface VisionCandidateLiveCorrectionEvidence extends VisionCandidateLiveCorrectionPayload {
  correction_id: string;
  raw_sha256: string;
  candidate_config_sha256: string;
  candidate_pipeline_fingerprint: Record<string, unknown>;
  production_prediction: Record<string, string>;
  candidate_prediction: Record<string, string>;
  candidate_runtime: Record<string, unknown>;
  candidate_debug: Record<string, unknown>;
  candidate_provenance: Record<string, unknown>;
  persisted_at: string;
}

export function persistVisionCandidateLiveCorrection(candidateId: string, payload: VisionCandidateLiveCorrectionPayload): Promise<VisionCandidateLiveCorrectionEvidence> {
  return api<VisionCandidateLiveCorrectionEvidence>(`/api/admin/vision-optimizer/admin-test-candidates/${encodeURIComponent(candidateId)}/live-test-corrections`, { method: "POST", body: JSON.stringify(payload) });
}

export function fetchVisionCandidateLiveCorrections(candidateId: string, limit = 20): Promise<VisionCandidateLiveCorrectionEvidence[]> {
  return api<VisionCandidateLiveCorrectionEvidence[]>(`/api/admin/vision-optimizer/admin-test-candidates/${encodeURIComponent(candidateId)}/live-test-corrections?limit=${Math.max(1, Math.min(100, Math.trunc(limit)))}`);
}


export interface VisionReleaseCandidate {
  id: string;
  created_at: string;
  source_job_id: string;
  parameter_set: string;
  status: "pending" | "approved" | "rejected" | "activated";
  decision_at: string | null;
  decision_by_user_id: number | null;
  admin_notification_required: boolean;
  notified_at: string | null;
  recommendation_label: string;
  summary: string;
  metrics: Record<string, unknown>;
  parameter_overrides: Record<string, unknown>;
  candidate_kind: "opencv_parameters" | "end_to_end";
  activation_supported: boolean;
  component_settings: Record<string, unknown>;
  candidate_config?: Record<string, unknown>;
  candidate_config_sha256?: string | null;
  validation?: Record<string, unknown>;
  promotion_gates?: Record<string, unknown>;
  release_acceptance?: Record<string, unknown>;
  shadow?: Record<string, unknown>;
  candidate_significance?: VisionCandidateSignificance | null;
  archive_state?: "active" | "archived";
  archived_at?: string | null;
  archived_reason?: string | null;
  linked_admin_test_candidate_id?: string | null;
}
export function fetchVisionReleaseCandidates(): Promise<VisionReleaseCandidate[]> {
  return api<VisionReleaseCandidate[]>("/api/admin/vision-optimizer/release-candidates");
}
export function fetchVisionReleaseCandidatesPage(page = 1, archived = false): Promise<VisionPagedResult<VisionReleaseCandidate>> { return api<VisionPagedResult<VisionReleaseCandidate>>(`/api/admin/vision-optimizer/release-candidates-page?page=${page}&page_size=10&archived=${archived ? "true" : "false"}`); }
export function setVisionReleaseCandidateArchived(id:string, archived:boolean):Promise<VisionReleaseCandidate>{ return api<VisionReleaseCandidate>(`/api/admin/vision-optimizer/release-candidates/${encodeURIComponent(id)}/archive`,{method:"POST",body:JSON.stringify({archived})}); }


export function decideVisionReleaseCandidate(id: string, decision: "approved" | "rejected"): Promise<VisionReleaseCandidate> {
  return api<VisionReleaseCandidate>(`/api/admin/vision-optimizer/release-candidates/${encodeURIComponent(id)}/decision`, { method: "POST", body: JSON.stringify({ decision }) });
}

export interface VisionOptunaSearchSpace {
  key: string;
  label: string;
  low: number;
  high: number;
  step: number | null;
  enabled: boolean;
  distribution: "float" | "int";
  group: string;
  scope: "opencv" | "hybrid" | "ocr";
}

export interface VisionOptunaStudyState {
  schema_version: number;
  mode: "opencv" | "hybrid" | "ocr";
  study_name: string;
  search_space_version: string;
  status: "foundation_ready" | "running" | "paused" | "completed" | "error";
  storage: string;
  objective: string;
  direction: "maximize";
  sampler: string;
  pruner: string;
  trial_count: number;
  best_value: number | null;
  best_trial_number: number | null;
  search_spaces: VisionOptunaSearchSpace[];
  runner_enabled: boolean;
  note: string;
  updated_at: string | null;
}

export function fetchVisionOptunaStudy(mode: "opencv" | "hybrid" | "ocr" = "hybrid"): Promise<VisionOptunaStudyState> {
  return api<VisionOptunaStudyState>(`/api/admin/vision-optimizer/optuna-study?mode=${mode}`, undefined, VISION_HEAVY_READ_TIMEOUT_MS);
}


export type VisionCnnStatus = "waiting_for_ground_truth" | "dataset_ready" | "training_ready" | "training" | "model_ready" | "validation_ready" | "runtime_incompatible" | "error";
export interface VisionCnnSyntheticSource {
  id: string;
  filename: string;
  sha256: string;
  storage_key: string;
  size_bytes: number;
  page_count: number;
  page_sizes: Array<{page?:number;width_pt?:number;height_pt?:number;aspect_ratio?:number}>;
  enabled: boolean;
  created_at: string | null;
  source_mode?: string | null;
  role?: string | null;
  built_in?: boolean;
  locked?: boolean;
  material_selection_seed?: string | null;
  material_master_storage_key?: string | null;
  material_master_sha256?: string | null;
  material_master_version?: number | null;
  material_master_requested_version?: number | null;
  material_master_fallback_used?: boolean;
  material_master_count?: number;
  layout_reference_sha256?: string | null;
  layout_version?: string | null;
}
export interface VisionDomainAdaptationState {
  version?: string; enabled?: boolean; source_split?: string; training_sample_count?: number; capture_quality_sample_count?: number; capture_quality_adapted?: boolean;
  min_train_samples?: number; blend_strength?: number; profile_sha256?: string | null; core_gs_used?: boolean; dev_used?: boolean; rolling_validation_used?: boolean;
  real_geometry?: Record<string, unknown>; real_capture_quality?: Record<string, unknown>; geometry_generator?: Record<string, number>; capture_generator?: Record<string, number>;
  safety?: { quality_guards_preserved?: boolean; hard_tail_preserved?: boolean; synthetic_is_gs?: boolean; production_activation?: boolean; note?: string };
}

export interface VisionCnnState {
  schema_version: number;
  lifecycle_version: string;
  status: VisionCnnStatus;
  dataset_version: string;
  total_keypoint_samples: number;
  training_samples: number;
  dev_samples: number;
  validation_samples: number;
  dataset_ready: boolean;
  training_ready: boolean;
  model_count: number;
  active_model_id: string | null;
  rejected_model_id?: string | null;
  architecture?: string | null;
  architecture_profile?: "baseline" | "compact" | "wide" | null;
  architecture_config?: Record<string, unknown> | null;
  runtime_parity?: Record<string, unknown> | null;
  last_checkpoint_at: string | null;
  last_validation_score: number | null;
  last_validation_corner_error: number | null;
  training_epoch: number;
  training_epochs: number;
  training_job_id: string | null;
  runtime: string | null;
  uncertainty: { version?: string; method?: string; brightness_factors?: number[]; aggregation?: string; metric?: string } | null;
  confidence_calibration: { version?: string; method?: string; source_split?: string; sample_count?: number; calibration_mae?: number; global_corner_error?: number; uncertainty_p90?: number } | null;
  synthetic_pretraining: { version?: string; source_count?: number; sample_count?: number; variants_per_page?: number; pretrain_epochs?: number; pretrain_loss?: number | null; completed?: boolean; real_finetune?: boolean; master_data_fill?: boolean; material_master_sha256?: string | null; material_master_version?: number | null; material_master_requested_version?: number | null; material_master_fallback_used?: boolean; material_master_count?: number; layout_reference_sha256?: string | null; layout_version?: string | null; curriculum_version?: string; curriculum_distribution?: Record<string,number>; curriculum_counts_per_page?: Record<string,number>; quality_guard_version?: string; quality_guard_required?: boolean; synthetic_is_ground_truth?: boolean; dev_source?: string } | null;
  synthetic_sources: VisionCnnSyntheticSource[];
  synthetic_source_count: number;
  synthetic_page_count: number;
  synthetic_variants_per_page: number;
  synthetic_pretrain_epochs: number;
  synthetic_sample_count: number;
  synthetic_ready: boolean;
  synthetic_material_master: {
    storage_key?: string; sha256?: string; requested_version?: number | null; used_version?: number | null;
    version_id?: number | null; source_hash?: string | null; source_kind?: string; fallback_used?: boolean;
    fallback_reason?: string | null; material_count?: number; created_at?: string | null;
  } | null;
  synthetic_layout_reference: {
    filename?: string; sha256?: string; storage_key?: string; role?: string; built_in?: boolean; locked?: boolean;
    training_source?: boolean; font_reference?: string;
  } | null;
  synthetic_curriculum: { version?: string; quality_guard_version?: string; domain_adaptation_version?: string; distribution?: Record<string,number>; counts_per_page?: { realistic?: number; medium?: number; hard?: number } } | null;
  domain_adaptation: VisionDomainAdaptationState | null;
  note: string;
}
export function fetchVisionCnnState(): Promise<VisionCnnState> {
  return api<VisionCnnState>("/api/admin/vision-optimizer/cnn-state");
}
export function uploadVisionCnnSyntheticSource(file: File): Promise<VisionCnnState> {
  const body = new FormData(); body.append("file", file);
  return api<VisionCnnState>("/api/admin/vision-optimizer/cnn-synthetic-sources", { method: "POST", body });
}
export function deleteVisionCnnSyntheticSource(sourceId: string): Promise<VisionCnnState> {
  return api<VisionCnnState>(`/api/admin/vision-optimizer/cnn-synthetic-sources/${encodeURIComponent(sourceId)}`, { method: "DELETE" });
}
export interface VisionSyntheticPreviewItem {
  id: string; variant: number; clean_url: string; scene_url: string; material_number?: string; material_text?: string; material_type?: string; manufacturer?: string; storage_address?: string; corners?: Array<{x:number;y:number}>; source_sha256?: string; material_master_sha256?: string; layout_reference_sha256?: string; curriculum_tier?: "realistic"|"medium"|"hard"; curriculum_version?: string; quality_guard?: { passed?: boolean; label_mean?: number; label_stddev?: number; area_fraction?: number; min_edge_px?: number }; transforms?: string[]; transform_parameters?: Record<string,unknown>;
}
export interface VisionSyntheticPreviewState { schema_version: number; version: string; generated_at?: string; preview_set_id?: string; count: number; variants_per_page?: number; curriculum_counts_per_page?: { realistic?: number; medium?: number; hard?: number }; items: VisionSyntheticPreviewItem[]; }
export function fetchVisionCnnSyntheticPreviews(): Promise<VisionSyntheticPreviewState> { return api<VisionSyntheticPreviewState>("/api/admin/vision-optimizer/cnn-synthetic-previews"); }

export interface VisionProductionState {
  active_profile_id: number | null;
  active_profile_name: string | null;
  previous_profile_id: number | null;
  previous_profile_name: string | null;
  active_bundle_id: string | null;
  previous_bundle_id: string | null;
  bundle_kind: "opencv_parameters" | "end_to_end" | null;
  cnn_model_id: string | null;
  activated_candidate_id: string | null;
  activated_at: string | null;
  runtime_ready: boolean;
  can_rollback: boolean;
  candidate_config_sha256?: string | null;
  bundle_version?: string | null;
  runtime_api?: string | null;
  bundle_payload_sha256?: string | null;
  package_export_ready?: boolean;
  last_rollback_mode?: "manual" | "automatic_technical" | null;
  last_rollback_reason?: string | null;
  last_rollback_at?: string | null;
}
export interface VisionProductionActivationIntent {
  schema_version: 2;
  contract_id: "production-mutation-intent-v2-sr40";
  intent_id: string;
  action: "activate-release-candidate" | "activate-imported-bundle" | "visual-lab-profile-production";
  target_kind: "release_candidate" | "imported_bundle" | "visual_lab_profile";
  target_id: string;
  target_fingerprint: string;
  candidate_id?: string | null;
  candidate_config_sha256?: string | null;
  target_profile_id?: number | null;
  target_bundle_id?: string | null;
  expected_active_profile_id: number | null;
  expected_active_bundle_id: string | null;
  shadow_snapshot_id?: string | null;
  promotion_gate_fingerprint?: string | null;
  created_by_user_id: number;
  created_at: string;
  expires_at: string;
  status: "prepared" | "committed" | "expired" | "superseded";
  committed_at: string | null;
  metadata?: Record<string, unknown>;
}
export interface VisionProductionActionResult { ok: boolean; message: string; production: VisionProductionState; candidate: VisionReleaseCandidate | null; }
export function fetchVisionProduction(): Promise<VisionProductionState> { return api<VisionProductionState>("/api/admin/vision-optimizer/production"); }
export interface VisionDashboardAction {
  required: boolean;
  level: "info" | "attention" | "admin_required" | "technical_blocker" | string;
  target: "overview" | "data" | "learning" | "results" | "production" | "activity" | string;
  headline: string;
  detail: string | null;
  decision_key?: string | null;
  severity?: "info" | "warning" | "error" | string;
  tone?: "cyan" | "green" | "orange" | "red" | "muted" | string;
  target_url?: string | null;
  state?: string | null;
}
export interface VisionDashboardCommandCenter {
  schema_version: number;
  action: VisionDashboardAction;
  state: { enabled:boolean; status:string; supervisor_state:string; phase:string; workflow_label:string|null; cycle:number; round:number };
  production: { active_profile_id:number|null; active_profile_name:string|null; active_bundle_id:string|null; runtime_ready:boolean; activated_candidate_id:string|null };
  quality: { metric:string; threshold:number; milestone_reached:boolean; terminal:boolean; production_rate:number|null; candidate_rate:number|null; delta:number|null; candidate_kind:string|null; candidate_id:string|null; candidate_status:string|null; candidate_label:string|null };
  activity: { job_id:string|null; job_type:string|null; status:string|null; label:string|null; progress:VisionOptimizerProgressSnapshot; heavy_active:number; heavy_limit:number; queued_count:number; active_jobs:Array<{ job_id:string|null; job_type:string|null; display_name?:string|null; status:string|null; label:string|null; completed_units:number; total_units:number }> };
}
export interface VisionDashboardDataSummary {
  schema_version: number;
  snapshot: { id:string|null; sha256:string|null; created_at:string|null; sampling_strategy:string|null; leakage_audit_passed:boolean; compatibility_mode:string|null };
  split_coverage: { training:number; dev:number; rolling_validation:number; core_gs:number; regression_bank:number; hard_case:number };
  domain_ocr_capability: { available_in_snapshot:boolean; available:number; dev:number; minimum_dev:number; shortfall:number; ready:boolean; split_version:string|null };
  condition_coverage: Array<{ slice:string; total:number; training:number; dev:number; rolling_validation:number; core_gs:number; regression_bank:number; hard_case:number }>;
  device_coverage: { available_in_compact_snapshot:boolean; detail_policy:string; note:string };
  capability_audit: { version:string|null; total:number; usable_total:number; quarantined_total:number; counts:Record<string,unknown>; fingerprint_sha256:string|null; generated_at:string|null };
  information_readiness: { version:string|null; ready:boolean; score:number; confidence:string; recommendation:string|null; signals:Array<Record<string,unknown>>; gaps:string[]; checked_at:string|null };
  capability_readiness: { data_limited:boolean; reason_code:string|null; reason:string|null; automatic_recovery:boolean; automatic_recovery_ready:boolean; train:number; dev:number; synthetic:number; compatible:number; excluded:number; global_readiness_ready:boolean; global_readiness_score:number; global_readiness_confidence:string };
  recommended_ground_truth: { new_since_last_cycle:number; required_hint:number; hard_cases_open:number; recommendation:string|null; detail_policy:string };
  rebaseline: { generation_version:string|null; baseline_generation_sha256:string|null; short_id:string|null; migration_pending:boolean; migration_mode:string|null; compatibility_counts:Record<string,number>; compatibility_label:string|null; compatibility_reason:string|null };
  provenance: { snapshot_id:string|null; dataset_sha256:string|null; capability_fingerprint_sha256:string|null; readiness_basis_sha256:string|null; learning_integrity_version:string|null };
}
export interface VisionLearningLoopSummary {
  loop: "FAST" | "SLOW" | "EXPLORATION" | string;
  planned_components: number;
  trial_budget: number;
  executed_trials: number;
  pruned_trials: number;
  reuse_count: number;
  skipped_count: number;
}
export interface VisionLearningSummary {
  schema_version: number;
  contract_version: string;
  state: { supervisor_state:string|null; status:string|null; phase:string|null; workflow_label:string|null; cycle_id:string|null; current_job_id:string|null; learning_round:number; focus:string[]; why:string|null };
  scheduler_loops: VisionLearningLoopSummary[];
  resources: { effective_cpu_cores:number|null; cpu_usage_cores:number|null; cpu_usage_percent:number|null; phase_current:number|null; phase_total:number|null; phase_unit:string|null; round_current:number|null; round_total:number|null; round_unit:string|null };
  population: { version:string|null; candidate_count:number; active_elites:Record<string,Record<string,unknown>>; component_champions:Record<string,Record<string,unknown>>; updated_at:string|null };
  genealogy: Array<{ experiment_id:string|null; parent_id:string|null; parent_experiment_id:string|null; parent_candidate_id:string|null; parent_source:string|null; component:string|null; generation:number; event_kind:string|null; engine:string|null; stage:string|null; hypothesis:Record<string,unknown>; metrics:Record<string,unknown>; outcome:string|null; rejection_reason:string|null; recorded_at:string|null; technical_failure:boolean; technical_failure_current:boolean; display_status:string; error_code:string|null; error_summary:string|null; known_fix_version:string|null; source_job_id:string|null; technical_detail_available:boolean; evidence_compatibility?:{class?:string|null;key?:string|null;label?:string|null;reason?:string|null;promotion_allowed?:boolean}|null }>;
  efficiency: { planned_components:number; skipped_components:number; trial_budget:number; executed_trials:number; pruned_trials:number; reused_components:number };
  rebaseline: { generation_version:string|null; baseline_generation_sha256:string|null; short_id:string|null; migration_pending:boolean; migration_mode:string|null; compatibility_counts:Record<string,number>; compatibility_label:string|null; compatibility_reason:string|null };
  provenance: { dataset_snapshot_id:string|null; round_workplan_id:string|null; learning_plan_version:string|null };
}
export interface VisionExperimentDetail { schema_version:number; experiment_id:string; parent_id:string|null; generation:number|null; outcome:string|null; recorded_at:string|null; identity:Record<string,unknown>; evidence:Record<string,unknown>; artifact_refs:string[]; rejection_reason:string|null; source_job_id?:string|null; job_detail?:{job_id?:string|null;job_type?:string|null;status?:string|null;attempt_count?:number|null;max_attempts?:number|null;retry_classification?:string|null;blocked_reason?:string|null;last_error?:string|null;message?:string|null;technical_failure_signature?:string|null;technical_failures?:Array<Record<string,unknown>>;result_error?:string|null;detail_unavailable?:boolean}|null; }
export function fetchVisionExperimentDetail(experimentId:string): Promise<VisionExperimentDetail> { return api<VisionExperimentDetail>(`/api/admin/vision-optimizer/experiments/${encodeURIComponent(experimentId)}`, { cache:"no-store" }); }

export interface VisionFailureHeatmapCategory {
  evidence_count: number;
  spatial_evidence_count: number;
  non_spatial_evidence_count: number;
  spatial_status: string | null;
  normalized_cells: number[];
  top_hotspots: Array<{ column?:number; row?:number; x?:number; y?:number; value?:number }>;
}
export interface VisionResultsSummary {
  schema_version: number;
  contract_version: string;
  comparison: {
    candidate_id:string|null; candidate_kind:string|null; candidate_status:string|null;
    production_four_of_four_exact_rate:number|null; candidate_four_of_four_exact_rate:number|null;
    production_per_field_exact_rate:Record<string,number>; candidate_per_field_exact_rate:Record<string,number>;
    hard_cases:{ wins:number; losses:number; ties:number; net_wins:number; sample_count:number };
    runtime:{ production_p95_ms:number|null; candidate_p95_ms:number|null; production_mean_ms:number|null; candidate_mean_ms:number|null; p95_available:boolean };
    win_loss_tie:{ wins:number; losses:number; ties:number; sample_count:number };
    remaining_error_reduction:number|null;
    regressions:{ field_total:number; per_field:Record<string,number>; critical:boolean };
    significance:{ reporting_band:string|null; exact_two_sided_p:number|null; paired_direction:string|null; warning:string|null; explanation:string|null; offer_recommended:boolean };
    provenance:{ source_job_id:string|null; cycle_id:string|null; learning_round:number|null; dataset_snapshot_id:string|null; candidate_config_sha256:string|null };
  };
  failure_heatmap: { contract_version:string|null; semantic_sha256:string|null; dataset_snapshot_id:string|null; grid:{columns?:number;rows?:number;coordinate_space?:string}; role:string; truth_allowed:boolean; promotion_evidence_allowed:boolean; production_dependency:boolean; categories:Record<string,VisionFailureHeatmapCategory> };
  reproducible_links:{ candidate_id:string|null; candidate_config_sha256:string|null; dataset_snapshot_id:string|null; failure_heatmap_sha256:string|null };
}

export interface VisionProductionUiSummary {
  schema_version: number;
  contract_version: string;
  active_production: { display_role:string; label:string; active_profile_id:number|null; active_profile_name:string|null; active_bundle_id:string|null; bundle_version:string|null; runtime_api:string|null; runtime_ready:boolean; bundle_payload_sha256:string|null; activated_candidate_id:string|null; activated_at:string|null };
  candidates: Array<{ candidate_id:string|null; kind:string; display_role:string; is_production:boolean; status:string|null; config_sha256:string|null; candidate_four_of_four_exact_rate:number|null; production_four_of_four_exact_rate:number|null; shadow:{ required:boolean; passed:boolean; status:string|null; observed_scans:number; minimum_scans:number; latest_snapshot_id:string|null } }>;
  candidate_live_test: { available:boolean; business_isolated:boolean; same_raw_required:boolean; correction_updates_same_run_score:boolean };
  device_runtime: { detail_policy:string; poll_in_dashboard:boolean; runtime_ready:boolean };
  activation: { manual_only:boolean; two_step:boolean; intent_bound:boolean; auto_promotion:boolean };
  rollback_history: { can_rollback:boolean; previous_profile_id:number|null; previous_profile_name:string|null; previous_bundle_id:string|null; last_mode:string|null; last_reason:string|null; last_at:string|null };
}
export interface VisionExpertUiSummary {
  schema_version: number;
  contract_version: string;
  inventory: {
    pipeline_genome:{target:string;available:boolean}; component_champions:{target:string;count:number}; engine_tournament:{target:string;available:boolean;contract:string}; experiment_memory:{target:string;recent_records:number}; jobs_queue:{target:string;active_count:number}; checkpoints:{target:string;detail_policy:string};
    cpu_ram:{target:string;cpu_usage_cores:number|null;memory_current_mb:number|null;memory_headroom_mb:number|null}; storage_gc:{target:string;state:string;automatic_unknown_cas_delete:boolean;heavy_vo_paused:boolean}; circuit_recovery:{target:string;supervisor_state:string|null;technical_blocked_jobs:number}; fingerprints:{target:string;production_sha256:string;learning_sha256:string;rebaseline_generation_sha256?:string|null;evidence_compatibility_version?:string|null}; diagnostics:{target:string;detail_policy:string}; failure_heatmaps:{target:string;semantic_sha256:string|null;truth_allowed:boolean}; statistics:{target:string;available_via_expert_system:boolean};
  };
  bounded: { jobs:number; experiments:number; candidates:number; dashboard_poll_heavy_detail_forbidden:boolean };
}

export interface VisionResearchEfficiencySummary {
  schema_version: number;
  version: string;
  updated_at?: string | null;
  total_jobs: number;
  total_compute_seconds: number;
  useful_jobs: number;
  relevant_gain_sum: number;
  validated_gain_sum?: number;
  total_trials?: number;
  executed_trials?: number;
  pruned_trials?: number;
  duplicate_experiments_avoided: number;
  dead_experiments_avoided: number;
  by_engine?: Record<string,{jobs:number;compute_seconds:number;useful_jobs:number;relevant_gain_sum:number;validated_gain_sum?:number;total_trials?:number;executed_trials?:number;pruned_trials?:number}>;
  by_phase?: Record<string,{jobs:number;compute_seconds:number;useful_jobs:number;relevant_gain_sum:number;validated_gain_sum?:number;total_trials?:number;executed_trials?:number;pruned_trials?:number}>;
  metrics?: {
    useful_job_rate?: number; gain_per_compute_hour?: number; compute_hours?: number; prune_rate?: number; execution_rate?: number;
    phase_outcome_rows?: number; surrogate_eligible_outcomes?: number; positive_phase_outcomes?: number; positive_phase_outcome_rate?: number;
    validated_phase_outcome_gain_sum?: number; validated_phase_gain_per_compute_hour?: number; proxy_transfer_mismatches?: number;
  };
}


export type VisionAuxiliaryResearchTask =
  | "cnn_rolling_calibration"
  | "evidence_repair"
  | "hard_case_critic"
  | "snapshot_precompute"
  | "search_space_scout"
  | "parent_child_evolution"
  | "seed_robustness"
  | "meta_learning"
  | "background_exploration";
export interface VisionAuxiliaryResearchResult {
  status: string;
  sample_count?: number;
  total?: number;
  missing_count?: number;
  missing_case_ref?: string | null;
  missing_reason?: string | null;
  retry_attempts?: number;
  next_retry_at?: string | null;
  next_action?: string | null;
  eligible_rolling_count?: number;
  excluded_rolling_count?: number;
  replacement_count?: number;
  replacement_candidate_count?: number;
  replacement_source_split?: string | null;
  pool_count?: number;
  pool_sha256?: string | null;
  missing_pool_slots?: number;
  case_count?: number;
  record_count?: number;
  proposal_id?: string | null;
  main_validation_required?: boolean | null;
  baseline_mean_objective?: number | null;
  candidate_mean_objective?: number | null;
  mean_objective_delta?: number | null;
  parameter_delta_percent?: number | null;
  objective_classification?: "positive" | "neutral" | "negative" | "unknown" | string | null;
  mutation?: { key?: string; group?: string | null; original?: number; value?: number; direction?: number } | null;
  input_fingerprint?: string | null;
  parent_count?: number;
  generation?: number;
  seed_run_count?: number;
  recipe_count?: number;
  seed_request_count?: number;
  recommendation_count?: number;
  missing_asset_count?: number;
  config_sha256?: string | null;
  exploration_mode?: string | null;
  did_work?: boolean | null;
  reason?: string | null;
  error?: string | null;
}
export interface VisionAuxiliaryResearchActivityItem {
  id: string;
  recorded_at: string;
  task: VisionAuxiliaryResearchTask | string;
  snapshot_id: string | null;
  cycle_id: string | null;
  result: VisionAuxiliaryResearchResult;
  research_only: boolean;
  production_activation_allowed: boolean;
  main_validation_required: boolean;
}
export interface VisionAuxiliaryResearchLiveActivity {
  phase: string;
  step: string;
  step_index: number;
  step_total: number;
  started_at: string | null;
  last_progress_at: string | null;
  runtime_seconds?: number | null;
  last_progress_age_seconds?: number | null;
  next_action: string | null;
  wait_reason: string | null;
  child_pid: number | null;
  child_active: boolean;
  completed_at?: string | null;
  result?: VisionAuxiliaryResearchResult | null;
}
export interface VisionAuxiliaryCalibrationState {
  sample_count: number;
  total: number;
  complete: boolean;
  missing_count: number;
  missing_case_ref: string | null;
  missing_reason: string | null;
  retry_attempts: number;
  next_retry_at: string | null;
  next_action: string | null;
  original_rolling_count: number;
  eligible_rolling_count: number;
  excluded_rolling_count: number;
  excluded_rolling_cases: Array<{ case_ref: string; reason: string }>;
  replacement_count: number;
  replacement_case_refs: string[];
  replacement_candidate_count: number;
  replacement_source_split: string | null;
  pool_count: number;
  pool_sha256: string | null;
  missing_pool_slots: number;
  updated_at: string | null;
}
export interface VisionAuxiliaryResearchArea {
  id: VisionAuxiliaryResearchTask | string;
  label: string;
  state: "working" | "error" | "observed" | "ready" | string;
  events: number;
  completed: number;
  partial: number;
  errors: number;
  last_status: string | null;
  last_activity_at: string | null;
  last_result: VisionAuxiliaryResearchResult | null;
  scheduler_state?: string | null;
  scheduler_status?: string | null;
  scheduler_reason?: string | null;
  next_eligible_at?: string | null;
}
export interface VisionAuxiliaryResearchOverview {
  schema_version: number;
  contract_version: string;
  worker: {
    state: string; raw_state: string; fresh: boolean; heartbeat_age_seconds: number | null; heartbeat_stale_after_seconds: number;
    updated_at: string | null; task: VisionAuxiliaryResearchTask | string | null; last_task: VisionAuxiliaryResearchTask | string | null;
    snapshot_id: string | null; cycle_id: string | null; case_ref: string | null; model_id: string | null;
    progress: { current?: number; total?: number; completed?: number; elapsed_seconds?: number; [key: string]: unknown } | null;
    mutation: { key?: string; group?: string | null; original?: number; value?: number; direction?: number } | null;
    last_result: VisionAuxiliaryResearchResult | null; last_error: string | null; last_error_at: string | null;
    pid: number | null;
    activity: VisionAuxiliaryResearchLiveActivity | null;
    last_activity_detail: VisionAuxiliaryResearchLiveActivity | null;
    idle_reason: string | null;
    next_action: string | null;
    next_check_at: string | null;
    next_scheduler_check_at: string | null;
    next_meaningful_work_at: string | null;
    last_real_progress_at: string | null;
    last_real_progress_age_seconds: number | null;
    scheduler_checks: Array<{ task: VisionAuxiliaryResearchTask | string; state: string; status: string; reason: string | null; next_eligible_at: string | null; did_work: boolean }>;
    calibration: VisionAuxiliaryCalibrationState | null;
  };
  areas: VisionAuxiliaryResearchArea[];
  statistics: {
    updated_at: string | null; events_total: number; counters: Record<string, number>;
    main_compute_saved_seconds: number | null; main_compute_saved_measurement: string;
  };
  last_activity: VisionAuxiliaryResearchActivityItem | null;
  last_real_activity: VisionAuxiliaryResearchActivityItem | null;
  activity_retention: { stored: number; max_items: number; bounded: boolean };
  resource_profile: { cpu_cores: number; torch_threads: number; priority: string; main_vo_has_priority: boolean };
  safety: {
    research_only: boolean; production_activation_allowed: boolean; candidate_promotion_allowed: boolean;
    ground_truth_mutation_allowed: boolean; core_gs_mutation_allowed: boolean; business_write_allowed: boolean;
    main_validation_required_for_proposals: boolean;
  };
}
export interface VisionAuxiliaryResearchHistoryPage extends VisionPagedResult<VisionAuxiliaryResearchActivityItem> {
  retention_max_items: number;
  bounded: boolean;
}

export interface VisionOverviewSnapshot {
  schema_version: number;
  contract_version?: string;
  generated_at: string;
  poll_after_ms?: number;
  control: VisionOptimizerControlState;
  autopilot: VisionAutopilotState;
  jobs: VisionOptimizerJobState[];
  admin_test_candidates: VisionAdminTestCandidate[];
  release_candidates: VisionReleaseCandidate[];
  latest_cycle_report: VisionCycleReport | null;
  production: VisionProductionState;
  bootstrap_policy_state: { override_enabled?: boolean; updated_at?: string | null; updated_by_user_id?: number | null };
  progress?: VisionOptimizerProgressSnapshot;
  command_center?: VisionDashboardCommandCenter;
  data_summary?: VisionDashboardDataSummary;
  learning_summary?: VisionLearningSummary;
  results_summary?: VisionResultsSummary;
  production_summary?: VisionProductionUiSummary;
  expert_summary?: VisionExpertUiSummary;
  research_efficiency?: VisionResearchEfficiencySummary;
}

let cachedVisionOverviewSnapshot: VisionOverviewSnapshot | null = null;
export function getCachedVisionOverviewSnapshot(): VisionOverviewSnapshot | null { return cachedVisionOverviewSnapshot; }
const VISION_OVERVIEW_SNAPSHOT_TIMEOUT_MS = VISION_HEAVY_READ_TIMEOUT_MS;
export async function fetchVisionOverviewSnapshot(signal?: AbortSignal): Promise<VisionOverviewSnapshot> {
  const value = await api<VisionOverviewSnapshot>("/api/admin/vision-optimizer/overview-snapshot", { cache:"no-store", signal }, VISION_OVERVIEW_SNAPSHOT_TIMEOUT_MS);
  cachedVisionOverviewSnapshot = value;
  return value;
}

export function fetchVisionAuxiliaryResearchOverview(signal?: AbortSignal): Promise<VisionAuxiliaryResearchOverview> {
  return api<VisionAuxiliaryResearchOverview>("/api/admin/vision-optimizer/auxiliary-research/overview", { cache:"no-store", signal });
}
export function fetchVisionAuxiliaryResearchHistory(page = 1, signal?: AbortSignal): Promise<VisionAuxiliaryResearchHistoryPage> {
  const safePage = Math.max(1, Math.trunc(page));
  return api<VisionAuxiliaryResearchHistoryPage>(`/api/admin/vision-optimizer/auxiliary-research/history?page=${safePage}&page_size=10`, { cache:"no-store", signal });
}


async function reconcileProductionRuntimeAfterAction(result: VisionProductionActionResult): Promise<VisionProductionActionResult> {
  const converged = await reconcileActiveVisualLabProfileAfterProductionChange(result.production.active_bundle_id);
  if (!converged && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("materialentnahme:toast", {
      detail: { message: "Production wurde serverseitig umgeschaltet. Der lokale Vision-Stand wird beim nächsten Online-Abgleich aktualisiert." },
    }));
  }
  return result;
}

export function prepareVisionReleaseCandidateActivation(id: string): Promise<VisionProductionActivationIntent> {
  return api<VisionProductionActivationIntent>(`/api/admin/vision-optimizer/release-candidates/${encodeURIComponent(id)}/activation-intent`, { method: "POST" });
}
export async function activateVisionReleaseCandidate(id: string, intentId: string): Promise<VisionProductionActionResult> {
  const result = await api<VisionProductionActionResult>(`/api/admin/vision-optimizer/release-candidates/${encodeURIComponent(id)}/activate`, { method: "POST", body: JSON.stringify({ intent_id: intentId }) });
  return reconcileProductionRuntimeAfterAction(result);
}
export async function rollbackVisionProduction(): Promise<VisionProductionActionResult> {
  const result = await api<VisionProductionActionResult>("/api/admin/vision-optimizer/production/rollback", { method: "POST" });
  return reconcileProductionRuntimeAfterAction(result);
}

export interface VisionRuntimeLatencyBucket {
  device_instance_id: string | null; device_label: string; device_class: string; platform: string | null; logical_cpu_count: number | null; device_memory_gb: number | null; installed_pwa: boolean | null; screen_width: number | null; screen_height: number | null; device_pixel_ratio: number | null;
  browser_family: string; browser_major_version: string | null; browser_engine: string; runtime_api: string | null; production_pipeline_sha256: string | null; detection_backend: string; detection_worker_state: string; ocr_backend: string; ocr_preprocess_runtime: string | null; ocr_worker_state: string; cnn_enabled: boolean | null; cnn_runtime: string | null; webgpu_available: boolean | null; cross_origin_isolated: boolean | null; shared_array_buffer_available: boolean | null; wasm_available: boolean | null; runtime_temperature: "cold" | "warm" | "unknown";
  sample_count: number; mean_shutter_to_step2_data_ready_ms: number | null; p50_shutter_to_step2_data_ready_ms: number | null; p95_shutter_to_step2_data_ready_ms: number | null; p99_shutter_to_step2_data_ready_ms: number | null;
}
export interface VisionProductionDriftMetrics { bundle_id: string | null; scan_count: number; correction_scan_count: number; correction_rate: number | null; target_field_ocr_correction_scan_count: number; target_field_ocr_correction_rate: number | null; retake_scan_count: number; retake_rate: number | null; retake_count: number; detection_failure_scan_count: number; detection_failure_rate: number | null; fast_path_fallback_scan_count: number; fast_path_fallback_rate: number | null; mean_processing_time_ms: number | null; p50_processing_time_ms: number | null; p95_processing_time_ms: number | null; recognition_latency_sample_count: number; mean_shutter_to_step2_data_ready_ms: number | null; p50_shutter_to_step2_data_ready_ms: number | null; p95_shutter_to_step2_data_ready_ms: number | null; p99_shutter_to_step2_data_ready_ms: number | null; cold_recognition_latency_sample_count: number; cold_p50_shutter_to_step2_data_ready_ms: number | null; cold_p95_shutter_to_step2_data_ready_ms: number | null; cold_p99_shutter_to_step2_data_ready_ms: number | null; warm_recognition_latency_sample_count: number; warm_p50_shutter_to_step2_data_ready_ms: number | null; warm_p95_shutter_to_step2_data_ready_ms: number | null; warm_p99_shutter_to_step2_data_ready_ms: number | null; runtime_telemetry_contract_version?: string; runtime_telemetry_sample_count?: number; runtime_telemetry_missing_sample_count?: number; runtime_latency_buckets?: VisionRuntimeLatencyBucket[]; }
export interface VisionProductionDriftState { schema_version: number; drift_version: string; status: "no_production" | "insufficient_data" | "stable" | "warning"; active: VisionProductionDriftMetrics; baseline: VisionProductionDriftMetrics | null; minimum_scans: number; reasons: string[]; manual_rollback_recommended: boolean; automatic_rollback: boolean; checked_at: string | null; }
export function fetchVisionProductionDrift(): Promise<VisionProductionDriftState> { return api<VisionProductionDriftState>("/api/admin/vision-optimizer/production/drift"); }

export interface VisionProductionBundleCheck {
  passed: boolean; bundle_id: string | null; bundle_version: string | null; runtime_api: string | null; bundle_payload_sha256: string | null; candidate_config_sha256: string | null; model_bundle_contract_sha256?: string | null;
  version_skew_passed?: boolean | null; engine_allowlist_passed?: boolean | null; runtime_smoke_passed?: boolean | null; local_installation_passed?: boolean | null;
  artifact_count: number; artifacts: Array<Record<string, unknown>>; reasons: string[]; checked_at: string | null;
}
export interface VisionProductionBundleImportResult { ok: boolean; message: string; bundle_id: string; verification: VisionProductionBundleCheck; }
export function fetchVisionProductionIntegrity(): Promise<VisionProductionBundleCheck> { return api<VisionProductionBundleCheck>("/api/admin/vision-optimizer/production/integrity"); }
export function prepareVisionProductionReleaseReady(): Promise<VisionProductionBundleCheck> { return api<VisionProductionBundleCheck>("/api/admin/vision-optimizer/production/prepare-release-ready", { method: "POST" }); }
export function runVisionProductionRoundtrip(): Promise<VisionProductionBundleCheck> { return api<VisionProductionBundleCheck>("/api/admin/vision-optimizer/production/roundtrip", { method: "POST" }); }
export async function exportVisionProductionBundle(): Promise<void> {
  const response = await fetchWithTimeout("/api/admin/vision-optimizer/production/export", { credentials: "include" }, 30000);
  if (!response.ok) { let message=`HTTP ${response.status}`; try { const payload=await response.json() as {detail?:string}; if(payload.detail)message=payload.detail; } catch {/* fallback */} throw new Error(message); }
  const blob=await response.blob(); const disposition=response.headers.get("content-disposition")||""; const matched=/filename="?([^";]+)"?/i.exec(disposition); const filename=matched?.[1]||"Vision_Production_Bundle.visionbundle.zip"; const url=URL.createObjectURL(blob); const link=document.createElement("a"); link.href=url; link.download=filename; document.body.appendChild(link); link.click(); link.remove(); window.setTimeout(()=>URL.revokeObjectURL(url),1000);
}
export async function importVisionProductionBundle(file: File): Promise<VisionProductionBundleImportResult> {
  const form=new FormData(); form.append("file", file);
  const response=await fetchWithTimeout("/api/admin/vision-optimizer/production/import", { method:"POST", credentials:"include", body:form }, 60000);
  if(!response.ok){let message=`HTTP ${response.status}`;try{const payload=await response.json() as {detail?:string};if(payload.detail)message=payload.detail;}catch{/* fallback */}throw new Error(message);}
  return response.json() as Promise<VisionProductionBundleImportResult>;
}
export function prepareImportedVisionProductionBundleActivation(bundleId:string): Promise<VisionProductionActivationIntent> {
  return api<VisionProductionActivationIntent>(`/api/admin/vision-optimizer/production/imported/${encodeURIComponent(bundleId)}/activation-intent`, {method:"POST"});
}
export async function activateImportedVisionProductionBundle(bundleId:string, intentId:string): Promise<VisionProductionActionResult> {
  const result = await api<VisionProductionActionResult>(`/api/admin/vision-optimizer/production/imported/${encodeURIComponent(bundleId)}/activate`, {method:"POST", body:JSON.stringify({intent_id:intentId})});
  return reconcileProductionRuntimeAfterAction(result);
}
export function runVisionOptunaStudy(trials = 12, mode: "opencv" | "hybrid" | "ocr" = "hybrid"): Promise<VisionOptimizerJobState> { return api<VisionOptimizerJobState>(`/api/admin/vision-optimizer/optuna-study/run?trials=${trials}&mode=${mode}`, { method: "POST" }); }



export interface VisionLiveState {
  schema_version: number; lifecycle_version: string; status: "waiting_for_data" | "dataset_ready" | "training" | "model_ready" | "validation_ready" | "candidate_ready" | "error";
  scope: "live"; live_dataset_id: string | null; source_snapshot_id: string | null; synthetic_source_count: number; real_session_count: number; real_frame_count: number; capture_positive_frame_count: number; real_negative_frame_count: number; browser_shadow_frame_count: number;
  active_evidence_acquisition?: { contract_id?: string; status?: string; requested_evidence?: string[]; missing?: Record<string, number>; targets?: Record<string, number>; observed?: { capture_positive_sessions?: number; capture_positive_frames?: number; verified_negative_frames?: number; browser_shadow_frames?: number; hard_cases?: number }; positive_evidence_ready?: boolean; full_real_validation_ready?: boolean; validation_minimum_ready?: boolean; model_runtime_ready?: boolean; normal_deploy_blocking?: boolean; production_recognition_blocking?: boolean; recommendation?: string; [key:string]: unknown };
  truth_contract_version: string; model_contract_version: string; evidence_class_counts: Record<string, number>; geometry_family_candidates: string[]; distractor_fixture_classes: string[];
  training_samples: number; dev_samples: number; validation_samples: number; hard_case_count: number; model_count: number; active_model_id: string | null; candidate_model_id: string | null;
  candidate_id: string | null; candidate_status: string | null; candidate_shadow_ready: boolean; candidate_production_eligible: boolean; runtime_parity_passed: boolean | null;
  training_epoch: number; training_epochs: number; last_validation_accuracy: number | null; last_axis_accuracy: Record<string, number>; real_capture_positive_finetune_frames: number; current_job_id: string | null; validation_job_id: string | null; last_real_validation_at: string | null;
  last_real_session_count: number; last_real_frame_count: number; last_real_cnn_ready_rate: number | null; last_real_temporal_ready_rate: number | null; last_real_heuristic_ready_rate: number | null; last_real_mean_confidence: number | null; last_real_axis_mean: Record<string, number>; last_real_quality_axis_agreement: Record<string, number>; last_browser_shadow_frame_count: number; last_browser_shadow_mean_score_delta: number | null; last_browser_shadow_max_score_delta: number | null; last_real_recommended: boolean | null;
  last_reference_runtime_p95_ms: number | null; last_real_negative_frame_count: number; last_real_negative_false_positive_rate: number | null; last_real_negative_precision_status: string | null; candidate_reasons: string[]; hard_case_sids: number[]; production_mode: "heuristic" | "shadow" | "candidate"; note: string;
}

export function fetchVisionLiveState(): Promise<VisionLiveState> { return api<VisionLiveState>("/api/admin/vision-optimizer/live-state"); }
export function runVisionLiveTraining(epochs = 8): Promise<VisionOptimizerJobState> { return api<VisionOptimizerJobState>(`/api/admin/vision-optimizer/live-train?epochs=${epochs}`, { method: "POST" }); }
export function runVisionLiveValidation(): Promise<VisionOptimizerJobState> { return api<VisionOptimizerJobState>("/api/admin/vision-optimizer/live-validate", { method: "POST" }); }

export type VisionOcrStatus = "waiting_for_data" | "dataset_ready" | "optimizing" | "result_ready" | "error";
export interface VisionOcrState {
  schema_version: number; lifecycle_version: string; status: VisionOcrStatus; dataset_version: string; total_samples: number;
  training_samples: number; dev_samples: number; validation_samples: number; confirmed_fields: number; study_name: string; trial_count: number; best_value: number | null;
  current_job_id: string | null; synthetic_version: string; synthetic_variants_per_page: number; synthetic_planned_samples: number; synthetic_trial_budget: number;
  synthetic_last_samples: number; synthetic_last_fields: number; synthetic_best_value: number | null; synthetic_baseline_value: number | null; synthetic_transfer_train_value: number | null;
  synthetic_transfer_accepted: boolean | null; synthetic_domain_gap: number | null; synthetic_is_ground_truth: boolean; synthetic_is_gs: boolean; production_changed: boolean; domain_adaptation: VisionDomainAdaptationState | null; note: string;
}
export function fetchVisionOcrState(): Promise<VisionOcrState> { return api<VisionOcrState>("/api/admin/vision-optimizer/ocr-state"); }
export function runVisionOcrOptimization(trials = 12): Promise<VisionOptimizerJobState> { return api<VisionOptimizerJobState>(`/api/admin/vision-optimizer/ocr-optimize?trials=${trials}`, { method: "POST" }); }

export interface VisionOptimizerSystemStatus { worker_state: string; worker_id?: string|null; worker_heartbeat_at?: string|null; worker_heartbeat_source?: "file"|"db_fallback"|string; worker_heartbeat_age_seconds?: number|null; worker_heartbeat_delayed?: boolean; worker_heartbeat_delay_seconds?: number; worker_heartbeat_stale_seconds?: number; worker_heartbeat_stale?: boolean; current_job_id?: string|null; circuit_state?: string; circuit_open_until?: string|null; circuit_last_event?:Record<string,unknown>|null; circuit_event_history?:Array<Record<string,unknown>>; consecutive_worker_failures?: number; retry_wait_jobs?: number; blocked_jobs?: number; reason: string | null; load_per_core: number | null; optimizer_load_per_effective_core?: number | null; free_memory_mb: number | null; total_memory_mb: number | null; used_memory_mb: number | null; temp_free_mb?: number | null; temp_used_mb?: number | null; temp_total_mb?: number | null; min_temp_headroom_mb?: number; cpu_cores: number | null; effective_cpu_cores?: number | null; container_cpu_limit_cores?: number | null; container_cpuset?: string | null; container_cpuset_cores?: number | null; container_cpu_usage_cores?: number | null; container_cpu_usage_percent?: number | null; resource_sample_age_seconds?: number|null; resource_sample_stale?: boolean; container_memory_current_mb?: number | null; container_memory_limit_mb?: number | null; container_memory_headroom_mb?: number | null; container_memory_reclaimable_mb?: number | null; container_memory_working_set_mb?: number | null; container_memory_effective_headroom_mb?: number | null; container_memory_anon_mb?: number | null; container_memory_file_mb?: number | null; container_memory_slab_reclaimable_mb?: number | null; container_pids_current?: number | null; container_pids_limit?: number | null; container_pids_headroom?: number | null; native_threads?: number | null; torch_threads?: number | null; resource_governor_version?: string|null; resource_governor_stage?: string|null; replay_worker_limit?: number|null; replay_worker_active?: number|null; replay_worker_peak?: number|null; heavy_worker_active?: number|null; estimated_native_threads?: number|null; oversubscription_protected?: boolean; process_rss_mb?: number|null; process_context_switches_voluntary?: number|null; process_context_switches_nonvoluntary?: number|null; last_child_walltime_ms?: number|null; last_replay_cases_per_second?: number|null; updated_at: string | null; mode: string; cpu_yield_threshold: number; min_free_memory_mb: number; min_container_memory_headroom_mb?: number; min_host_free_memory_mb?: number; min_pid_headroom?: number; nice: number; hard_cpu_limit?: string; hard_cpuset?: string | null; hard_memory_limit?: string; hard_pids_limit?: number; worker_started_at?: string | null; uptime_seconds?: number | null; resource_history?: Array<{at:string;state:string;load_per_core:number|null;free_memory_mb:number|null;total_memory_mb:number|null;temp_free_mb?:number|null;temp_used_mb?:number|null;container_memory_headroom_mb?:number|null;container_memory_effective_headroom_mb?:number|null;container_memory_working_set_mb?:number|null;container_memory_reclaimable_mb?:number|null;container_pids_headroom?:number|null}>; hardening_recent_jobs?: number; hardening_failed_jobs?: number; hardening_mean_job_duration_ms?: number | null; hardening_last_job?: {job_id:string;type:string;scope:string;status:string;duration_ms:number|null;finished_at:string}|null; heavy_child_healthy?: boolean; heavy_child_work_healthy?: boolean; heavy_child_hung?: boolean; heavy_child_hang_reason?: string|null; heavy_child_progress_age_seconds?: number|null; heavy_child_cpu_idle_seconds?: number|null; heavy_child_invocation_runtime_seconds?: number|null; heavy_child_cpu_ticks?: number|null; heavy_child_hang_policy?: {kind?:string;no_progress_seconds?:number|null;max_runtime_seconds?:number|null;[key:string]:unknown}; heavy_child_stage?: string|null; heavy_child_pid?: number|null; heavy_child_age_seconds?: number|null; heavy_child_progress?: {phase?:string;epoch?:number;epochs?:number;pretrain_epoch?:number;pretrain_epochs?:number;batch_current?:number;batch_total?:number;active_batch?:number;effective_torch_threads?:number;epoch_elapsed_seconds?:number;elapsed_seconds?:number;checkpoint_status?:string;resumed_from_batch?:number;pause_requested?:boolean;[key:string]:unknown}; storage_governor_state?:string; storage_governor_at?:string|null; storage_free_mb?:number|null; storage_total_mb?:number|null; storage_used_mb?:number|null; storage_min_free_mb?:number|null; storage_admission_floor_mb?:number|null; storage_warning_floor_mb?:number|null; storage_throttle_recommended?:boolean; storage_headroom_margin_mb?:number|null; storage_forecast_growth_mb?:number|null; storage_forecast_raw_growth_mb?:number|null; storage_forecast_cap_mb?:number|null; storage_forecast_p95_mb_per_hour?:number|null; storage_forecast_observation_hours?:number|null; storage_forecast_horizon_hours?:number|null; storage_forecast_source?:string|null; storage_heavy_vo_pause_required?:boolean; resource_policy_contract?:string|null; resource_policy_id?:string|null; resource_policy_sha256?:string|null; resource_policy_mode?:"auto"|"custom"|string; configured_cpu_budget_cores?:number|null; configured_memory_budget_mb?:number|null; configured_storage_reserve_mb?:number|null; configured_storage_reserve_percent?:number|null; effective_cpu_budget_cores?:number|null; effective_memory_budget_mb?:number|null; effective_replay_worker_max?:number|null; effective_ocr_worker_max?:number|null; effective_chromium_slot_limit?:number|null; effective_torch_threads?:number|null; configured_heavy_job_limit?:number|null; resource_target_profile?:string|null; auto_storage_reserve_mb?:number|null; auto_storage_reserve_percent?:number|null; top_level_heavy_scheduler_contract?:string|null; top_level_heavy_scheduler_version?:string|null; top_level_heavy_job_limit?:number|null; top_level_heavy_jobs_active?:number|null; top_level_heavy_jobs?:Array<{id?:string|null;type?:string|null;class?:string|null;started_at?:string|null;cpu_budget_cores?:number|null;memory_budget_mb?:number|null}>; top_level_heavy_draining_for_recycle?:boolean; }
export interface VisionOptimizerActivitySystem { worker_state:string; reason:string|null; current_job_id:string|null; active_job_ids:string[]; top_level_heavy_jobs_active:number; top_level_heavy_job_limit:number|null; worker_id:string|null; worker_heartbeat_at:string|null; worker_heartbeat_age_seconds:number|null; worker_heartbeat_stale:boolean; circuit_state:string; circuit_open_until:string|null; circuit_last_event?:Record<string,unknown>|null; circuit_event_history?:Array<Record<string,unknown>>; }
export interface VisionOptimizerActivityAutopilot { enabled:boolean; status:string; phase:string; supervisor_state:VisionSupervisorState|string; current_job_id:string|null; learning_round:number; max_learning_rounds:number; workflow_step:number; workflow_label:string; last_message:string|null; updated_at:string|null; }
export interface VisionOptimizerProgressLevel { key:string|null; label:string; percent:number|null; current:number|null; total:number|null; unit:string|null; status:string|null; detail:string|null; plan?:Record<string,unknown>; search_attempt_current?:number|null; search_attempt_total?:number|null; optuna_trial_display_id?:number|null; optuna_study_name?:string|null; optuna_study_generation?:string|null; eta_seconds?:number|null; eta_confidence?:"low"|"medium"|"high"|string|null; eta_basis?:string|null; eta_ready?:boolean; }
export interface VisionOptimizerProgressAttempt { current:number; total:number; label:string|null; }
export interface VisionOptimizerProgressSnapshot { schema_version:number; contract_version:string; workplan_version:string; workplan_sha256:string; round_workplan_id?:string|null; cycle_workplan_id?:string|null; scope_revision?:number; cycle:VisionOptimizerProgressLevel; round:VisionOptimizerProgressLevel; phase:VisionOptimizerProgressLevel; subphase:VisionOptimizerProgressLevel; attempt:VisionOptimizerProgressAttempt; plan:Array<Record<string,unknown>>; scope_change:string|null; monotonic_scope_key:string; }
export interface VisionOptimizerActivitySnapshot { schema_version:number; contract_version?:string; generated_at:string; poll_after_ms:number; autopilot:VisionOptimizerActivityAutopilot; job:VisionOptimizerJobState|null; jobs:VisionOptimizerJobState[]; system:VisionOptimizerActivitySystem; progress:VisionOptimizerProgressSnapshot; }
export function fetchVisionOptimizerActivitySnapshot(signal?: AbortSignal, timeoutMs = 8000): Promise<VisionOptimizerActivitySnapshot> { return api<VisionOptimizerActivitySnapshot>("/api/admin/vision-optimizer/activity", { signal, cache:"no-store" }, timeoutMs); }

export function fetchVisionOptimizerSystemStatus(): Promise<VisionOptimizerSystemStatus> { return api<VisionOptimizerSystemStatus>("/api/admin/vision-optimizer/system-status"); }
export interface VisionOptimizerResourcePolicy {
  schema_version: number; contract: string; policy_version: string; resource_policy_id: string; resource_policy_sha256: string;
  mode: "auto"|"custom"; cpu_budget_cores: number|null; memory_budget_mb: number|null; storage_reserve_mb: number|null; storage_reserve_percent: number|null; heavy_job_limit: number|null;
  target_profile: string; auto_storage_reserve_mb: number; auto_storage_reserve_percent: number;
  updated_at: string|null; updated_by_user_id: number|null;
}
export interface VisionOptimizerResourcePolicyPayload { mode:"auto"|"custom"; cpu_budget_cores:number|null; memory_budget_mb:number|null; storage_reserve_mb:number|null; storage_reserve_percent:number|null; heavy_job_limit:number|null; }
export function fetchVisionOptimizerResourcePolicy(): Promise<VisionOptimizerResourcePolicy> { return api<VisionOptimizerResourcePolicy>("/api/admin/vision-optimizer/resource-policy"); }
export function updateVisionOptimizerResourcePolicy(payload: VisionOptimizerResourcePolicyPayload): Promise<VisionOptimizerResourcePolicy> { return api<VisionOptimizerResourcePolicy>("/api/admin/vision-optimizer/resource-policy", {method:"PUT", body:JSON.stringify(payload)}); }

